import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    UploadSimple,
    DownloadSimple,
    Prohibit,
    Sparkle,
    ChartBar,
    Trash,
    FileText,
    FilePdf,
    ArrowsClockwise,
    Warning,
    ListChecks,
    Plus,
    ArrowClockwise,
    CloudArrowUp,
    Eye,
    EyeSlash,
    ShieldCheck,
} from "@phosphor-icons/react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { AdsImportBar } from "@/components/AdsImportBar";
import {
    adsSearchTerms,
    adsAuditGet,
    adsSearchTermsScore,
    adsSearchTermsSync,
    adsSearchTermsProcessed, errText,
} from "@/lib/api";

/* ------------------------------------------------------------------------- */
/* CSV parsing — RFC-4180ish, tolerant of BOM + CRLF                          */
/* ------------------------------------------------------------------------- */

const stripBom = (s) => s.replace(/^\uFEFF/, "");

// Simple CSV → 2D array parser. Handles quoted fields with embedded commas + escaped quotes.
const parseCsv = (text) => {
    const rows = [];
    let cur = [];
    let field = "";
    let inQuotes = false;
    const s = stripBom(text);
    for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (inQuotes) {
            if (ch === '"' && s[i + 1] === '"') {
                field += '"';
                i++;
            } else if (ch === '"') {
                inQuotes = false;
            } else {
                field += ch;
            }
        } else {
            if (ch === '"') inQuotes = true;
            else if (ch === ",") {
                cur.push(field);
                field = "";
            } else if (ch === "\r") {
                /* skip */
            } else if (ch === "\n") {
                cur.push(field);
                rows.push(cur);
                cur = [];
                field = "";
            } else field += ch;
        }
    }
    if (field.length > 0 || cur.length > 0) {
        cur.push(field);
        rows.push(cur);
    }
    return rows;
};

const toCsv = (rows) =>
    rows
        .map((row) =>
            row
                .map((v) => {
                    const s = String(v ?? "");
                    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
                    return s;
                })
                .join(",")
        )
        .join("\n");

const downloadCsv = (filename, content) => {
    const blob = new Blob(["\uFEFF" + content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

/* ------------------------------------------------------------------------- */
/* Flexible header mapping                                                    */
/* ------------------------------------------------------------------------- */

// Match Google Ads header aliases across locales/exports.
const HEADER_ALIASES = {
    search_term: [
        "search term",
        "search terms",
        "query",
        "search query",
        "consulta",
        "consulta de búsqueda",
        "término de búsqueda",
        "recherche",
        "requête",
        "requête de recherche",
        "suchbegriff",
        "suchanfrage",
    ],
    added_excluded: [
        "added/excluded",
        "added / excluded",
        "keyword status",
        "status",
        "match type",
        "estado",
        "statut",
        "status der keywords",
    ],
    campaign: ["campaign", "campaign name", "campaña", "campagne", "kampagne"],
    ad_group: ["ad group", "ad group name", "grupo de anuncios", "groupe d'annonces", "anzeigengruppe"],
    impressions: ["impressions", "impresiones", "impressionen"],
    clicks: ["clicks", "clics", "klicks"],
    cost: ["cost", "spend", "coste", "coût", "kosten"],
    ctr: ["ctr", "click-through rate", "click through rate", "clic à travers taux"],
    conversions: ["conversions", "conv.", "conv", "conversiones"],
    cost_per_conv: [
        "cost / conv.",
        "cost/conv.",
        "cost per conv.",
        "cost per conversion",
        "cpa",
        "coste/conv.",
        "coût/conv.",
        "kosten/conv.",
    ],
    conv_rate: ["conv. rate", "conv rate", "conversion rate"],
    avg_cpc: ["avg. cpc", "avg cpc", "average cpc", "cpc medio", "cpc moyen"],
};

const normalizeHeader = (h) =>
    String(h || "")
        .replace(/\uFEFF/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

const findHeaderIndex = (headers, key) => {
    const normalized = headers.map(normalizeHeader);
    const aliases = HEADER_ALIASES[key] || [];
    for (const a of aliases) {
        const idx = normalized.indexOf(a.toLowerCase());
        if (idx !== -1) return idx;
    }
    // Loose contains-match as a final fallback (e.g. "Cost (USD)" matches "cost")
    // Only allow it for multi-cell rows so it doesn't misidentify a single-cell title row.
    if (normalized.length < 3) return -1;
    for (let i = 0; i < normalized.length; i++) {
        for (const a of aliases) {
            if (normalized[i].startsWith(a.toLowerCase() + " ") || normalized[i] === a.toLowerCase()) return i;
        }
    }
    return -1;
};

const parseNumber = (raw) => {
    if (raw === null || raw === undefined) return 0;
    let s = String(raw).trim();
    if (!s || s === "--" || s === "—") return 0;
    // Strip currency symbols, spaces, thousands separators. Handle either . or , as decimal.
    s = s.replace(/[£$€₹A-Za-z\s]/g, "");
    // If both , and . present, assume . is decimal, , is thousands.
    if (s.includes(",") && s.includes(".")) {
        s = s.replace(/,/g, "");
    } else if (s.includes(",") && !s.includes(".")) {
        // European decimal comma
        s = s.replace(/\./g, "").replace(",", ".");
    }
    // Strip trailing % (CTR/conv rate) — caller converts back if needed.
    s = s.replace(/%$/, "");
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
};

/* ------------------------------------------------------------------------- */
/* Negative signal patterns                                                   */
/* ------------------------------------------------------------------------- */

// Hard blocklist patterns per the spec. Word-boundary regex so "diyer" isn't caught by "diy".
// NOTE: "free" is handled separately (see FREE_STANDALONE_RE) because it has
// context-sensitive semantics — "chemical-free pest control" is a quality
// modifier, not a low-intent free-service request.
const NEGATIVE_PATTERNS = [
    { label: "jobs", re: /\bjobs?\b/i },
    { label: "salary", re: /\bsalary\b/i },
    { label: "diy", re: /\bdiy\b/i },
    { label: "how to", re: /\bhow\s*to\b/i },
    { label: "tutorial", re: /\btutorial\b/i },
];

// "free" as a compound service modifier vs a low-intent "gimme a freebie" signal.
// We exclude the compound-modifier case first, then match a naked "free".
// Words that make "X-free / X free" mean "without X" (a quality descriptor).
// Includes chemistry/quality terms AND pest-type nouns ("insect free", "bug
// free", "rodent free") which are customer outcome language, not low-intent
// "freebie" searches. Also includes service nouns ("inspection free" from
// "free inspection" reordered by broad match).
const FREE_COMPOUND_MODIFIERS = [
    // Chemistry / substances
    "chemical", "chemicals", "chem", "toxin", "toxic", "non-toxic", "non", "nontoxic",
    "pesticide", "pesticides", "poison", "poisons", "smell", "smoke", "odour", "odor",
    "dust", "mess", "residue", "ammonia", "chlorine", "bpa",
    // Contractual / cost — still valid signals of quality, not "gimme freebie"
    "obligation", "contract", "commitment", "hidden", "cost",
    // Quality-of-life descriptors
    "tension", "worry", "hassle", "stress", "gluten", "sugar", "dairy",
    "latex", "allergen", "allergy",
    // Pest-type nouns → "insect free", "bug free", "rodent free" describe the
    // outcome the customer wants. These are HIGH-intent phrasings.
    "insect", "insects", "pest", "pests", "bug", "bugs", "bedbug", "bedbugs",
    "cockroach", "cockroaches", "roach", "roaches", "termite", "termites",
    "rodent", "rodents", "rat", "rats", "mouse", "mice", "mosquito", "mosquitoes",
    "fly", "flies", "ant", "ants", "spider", "spiders", "wasp", "wasps",
    "lizard", "lizards", "mite", "mites", "flea", "fleas", "silverfish",
    "worm", "worms", "germ", "germs", "bacteria", "virus", "moth", "moths",
    // Service nouns → "inspection free" typically comes from "free inspection"
    // being broad-matched; treat as compound modifier so we don't kill leads.
    "inspection", "quote", "estimate", "consultation", "callout", "visit",
];
// Escape and join into an alternation.
const FREE_COMPOUND_RE = new RegExp(
    // "chemical free" or "chemical-free" (either whitespace or hyphen between)
    `\\b(?:${FREE_COMPOUND_MODIFIERS.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})[\\s-]+free\\b`,
    "i",
);
// Any hyphenated "\w+-free" also indicates a quality descriptor (e.g. "cost-free" edge cases).
const HYPHENATED_FREE_RE = /[a-z]-free\b/i;
const isCompoundFreeModifier = (term) =>
    FREE_COMPOUND_RE.test(term) || HYPHENATED_FREE_RE.test(term);

// Currency formatter — always uses the currency of the account under audit,
// never a hardcoded symbol. Falls back to a bare number if the currency
// code is unknown.
const formatMoney = (amount, currency) => {
    const c = (currency || "").trim().toUpperCase();
    try {
        return new Intl.NumberFormat(undefined, {
            style: c ? "currency" : "decimal",
            currency: c || undefined,
            maximumFractionDigits: 2,
        }).format(Number(amount) || 0);
    } catch {
        const sym = { USD: "$", GBP: "£", EUR: "€", INR: "₹", AUD: "A$", CAD: "C$" }[c] || "";
        return `${sym}${(Number(amount) || 0).toFixed(2)}`;
    }
};
const currencySymbol = (currency) => {
    const c = (currency || "").trim().toUpperCase();
    return { USD: "$", GBP: "£", EUR: "€", INR: "₹", AUD: "A$", CAD: "C$" }[c] || (c || "");
};

// Explicit named-competitor / directory heuristics. These carry the highest
// confidence for negation because they refer to a specific rival brand or
// aggregator, not the client's core market.
const DIRECTORY_TOKENS = [
    "justdial", "just dial", "sulekha", "urban company", "urbanclap",
    "housejoy", "practo", "quikr", "indiamart", "yelp", "yellowpages",
];

// Category-descriptor stopwords. If a term ending in a brand suffix (e.g.
// "... pest control") consists ONLY of these tokens in the prefix, it is a
// generic core-market phrase, NOT a named competitor. Previous version of
// the heuristic flagged "chemical free pest control", "bangalore pest
// control", "mosquito pest control", "best pest control" as competitor
// brands — this stopword set fixes the false positive at the root.
const CATEGORY_DESCRIPTOR_TOKENS = new Set([
    // Quality / rating (customer intent, not competitor)
    "best", "top", "trusted", "good", "great", "professional", "expert",
    "certified", "reliable", "reputable", "leading", "premier", "premium",
    "quality", "excellent", "highly", "recommended", "rated", "reviewed",
    // Method / eco / safety modifiers (positive intent, NOT competitor)
    "natural", "herbal", "organic", "ayurvedic", "sustainable", "eco",
    "green", "safe", "gentle", "odorless", "odourless", "smokeless",
    "chemical", "chemicals", "nontoxic", "nonchemical", "biological", "bio",
    "botanical", "harmless", "kid", "kids", "child", "children", "pet",
    "pets", "family", "friendly",
    // Location descriptors (Indian cities/regions common in this account)
    "bangalore", "bengaluru", "mumbai", "chennai", "delhi", "hyderabad",
    "pune", "kolkata", "ahmedabad", "kochi", "cochin", "jaipur", "lucknow",
    "surat", "tamilnadu", "karnataka", "kerala", "maharashtra", "gujarat",
    "indian", "india", "south", "north", "east", "west", "central",
    // Property / room descriptors
    "home", "house", "kitchen", "office", "bathroom", "bedroom",
    "residential", "commercial", "domestic", "apartment", "flat", "villa",
    "building", "society", "hostel", "restaurant", "hotel", "warehouse",
    "shop", "store", "godown", "factory", "school", "hospital",
    // Adjacent pest types
    "mosquito", "mosquitoes", "lizard", "lizards", "ant", "ants",
    "spider", "spiders", "silverfish", "termite", "termites",
    "cockroach", "cockroaches", "rat", "rats", "rodent", "rodents",
    "bed", "bug", "bugs", "bedbug", "bedbugs", "flea", "fleas",
    "insect", "insects", "roach", "roaches", "wasp", "wasps",
    "fly", "flies", "moth", "moths", "mite", "mites",
    // Generic quality/price
    "general", "standard", "basic", "advanced", "instant", "quick", "fast",
    "easy", "cheap", "affordable", "low", "cost", "price", "budget",
    "same", "day", "night", "immediate", "urgent", "emergency", "one",
    "time", "monthly", "annual", "yearly", "weekly", "hourly",
    // Connectors
    "and", "or", "for", "with", "the", "a", "an", "to", "of", "in",
    "at", "on", "by", "not", "no", "yes", "is", "are",
    // Action verbs
    "get", "book", "find", "buy", "hire", "call", "need", "want", "do",
    "does", "how", "much", "cheap",
    // Compound service words
    "gel", "spray", "treatment", "solution", "solutions", "removal",
    "extermination", "fumigation", "inspection", "cleanup", "cleaning",
    "sanitization", "disinfection", "prevention", "protection",
    "management", "kill", "killer", "killing",
    // Position / availability
    "urban", "rural", "local", "nearby", "near", "nearest", "online",
    "offline", "small", "big", "large", "mini", "micro", "24", "hours",
    // Anti / poison substances
    "anti", "poison", "poisons", "powder", "liquid", "tablet", "tablets",
    "gas", "aerosol", "trap", "traps",
]);

// Positive-intent modifiers. If ANY of these appear in the term, the term
// signals a HIGH-VALUE customer intent and MUST NOT be flagged as a
// negative under any rule. This is a hard block, like the own-brand guard.
// Examples the auditor was wrongly negativizing before this fix:
//   "chemical free pest control" (customer explicitly wanting non-chemical)
//   "herbal pest control services"  (customer explicitly wanting herbal)
//   "professional cockroach pest control" (high-intent buyer signal)
//   "safe pest control services" (customer prioritising safety)
const POSITIVE_INTENT_RE = new RegExp(
    "\\b(?:" +
    [
        // Method / eco (customer explicitly wants a specific method)
        "chemical[\\s-]?free", "chem[\\s-]?free", "pesticide[\\s-]?free",
        "poison[\\s-]?free", "toxin[\\s-]?free", "non[\\s-]?toxic",
        "nontoxic", "odou?rless", "smokeless", "smell[\\s-]?free",
        "natural", "herbal", "organic", "ayurvedic", "sustainable",
        "eco", "eco[\\s-]?friendly", "green", "safe", "biological",
        "botanical", "harmless", "kid[\\s-]?safe", "pet[\\s-]?safe",
        "child[\\s-]?safe", "family[\\s-]?safe",
        // Buyer-intent qualifiers (premium / evaluated purchase)
        "professional", "certified", "licensed", "insured",
        "accredited", "experienced", "expert",
        // Universal compound SERVICE-ATTRIBUTE modifiers. Per audit rule
        // #8: hyphen/space-separated compounds like "self-drive", "same-day",
        // "24-hour", "at-home" describe a service attribute the customer
        // wants — not low intent. These apply across every local-service
        // vertical (rentals, home services, appliance repair, etc.).
        "self[\\s-]?drive", "self[\\s-]?service",
        "same[\\s-]?day", "next[\\s-]?day", "on[\\s-]?time",
        "24[\\s-]?hour", "24[\\s-]?hr", "24[\\s-]?7", "24x7",
        "on[\\s-]?demand", "on[\\s-]?call", "on[\\s-]?site",
        "at[\\s-]?home", "in[\\s-]?home", "home[\\s-]?visit",
        "door[\\s-]?step", "doorstep", "home[\\s-]?service",
        "one[\\s-]?way", "round[\\s-]?trip",
        "no[\\s-]?contact", "contact[\\s-]?less", "contactless",
        "cash[\\s-]?less", "cashless",
        "pre[\\s-]?paid", "post[\\s-]?paid",
        "walk[\\s-]?in",
    ].join("|") +
    ")\\b",
    "i"
);
const hasPositiveIntent = (term) => POSITIVE_INTENT_RE.test(term || "");

// ---------------------------------------------------------------------------
// Product / service-line taxonomy for LOCAL SERVICE VERTICALS.
// Rule #4 (universal audit): "[product/service type] + [service verb]" is
// by definition the CLIENT'S PRODUCT LINE, not noise. Hard-block ALL such
// combinations regardless of individual spend / conv, across every vertical
// this tool serves — pest control, appliance repair, vehicle rental,
// tank/sump cleaning, home services, plumbing, electrical, etc.
//
// This is the SEED set. The component ALSO derives account-specific
// tokens from the current CSV's campaign + ad-group names (see
// `derivedProductTokens` useMemo) and merges them at classification time,
// so a client whose ad groups are named "Bed Bug", "Cockroach Control",
// "Tempo Traveller Mysore", "Washing Machine Repair" etc. automatically
// gets its own product lines protected without a code change.
// ---------------------------------------------------------------------------
const PRODUCT_TAXONOMY_TOKENS = new Set([
    // === Pest control vertical ===
    "cockroach", "cockroaches", "roach", "roaches", "roachs",
    "bedbug", "bedbugs",
    "rat", "rats", "rodent", "rodents", "mouse", "mice",
    "termite", "termites",
    "mosquito", "mosquitoes", "mosquitos",
    "ant", "ants",
    "spider", "spiders",
    "silverfish", "silverfishes",
    "flea", "fleas",
    "lizard", "lizards",
    "wasp", "wasps",
    "moth", "moths",
    "mite", "mites",
    "snake", "snakes",
    "worm", "worms",
    "beetle", "beetles",
    "fly", "flies", "housefly",
    "scorpion", "scorpions",
    "borer", "borers",
    "weevil", "weevils",
    "bug", "bugs",
    "german", "american", "oriental", "smokybrown", "brownbanded",
    "asian", "surinam",
    // Pest situations / substrates
    "dead",
    "wood", "wooden", "timber", "furniture", "cabinet", "cupboard",
    "sofa", "couch", "mattress", "carpet", "curtain",
    "wall", "walls", "ceiling", "floor", "floors",
    "attic", "basement",
    "infestation", "invasion", "outbreak",
    // === Appliance repair vertical ===
    "washing", "machine", "washer", "dryer",
    "fridge", "refrigerator", "freezer",
    "microwave", "oven", "stove", "cooktop",
    "dishwasher", "dish",
    "ac", "aircon", "airconditioner", "conditioner", "hvac",
    "geyser", "heater", "boiler",
    "chimney", "hood", "exhaust",
    "mixer", "grinder", "blender", "juicer", "toaster",
    "purifier", "ro", "uv",
    "tv", "television", "led", "lcd",
    "laptop", "desktop", "computer", "pc", "printer", "monitor",
    "iron", "sewing",
    // Appliance fault descriptions
    "leaking", "leak", "broken", "damaged", "faulty",
    "error", "code", "spinning", "draining",
    "cooling", "heating", "drum", "motor", "compressor",
    "filter", "pump", "belt", "gasket", "thermostat",
    "gas", "electric",
    // NOTE: appliance / vehicle BRAND names (samsung, lg, godrej, toyota,
    // maruti, …) are intentionally NOT in the universal seed because they
    // collide across verticals — "godrej pest control" is a real
    // pest-control competitor (Godrej HiCare) even though Godrej also
    // makes appliances. Vertical-specific brand tokens should ride in via
    // the `derivedProductTokens` set the component builds from the
    // account's own ad-group + campaign names (see `derivedProductTokens`
    // useMemo in the component).
    // === Vehicle / rental vertical ===
    "tempo", "traveller", "traveler",
    "van", "minivan",
    "bus", "minibus", "coach",
    "suv", "sedan", "hatchback",
    "cab", "taxi",
    "car", "cars",
    "bike", "bikes", "scooter", "scooty",
    "tractor", "truck", "lorry",
    "seater", "seaters",
    "outstation", "oneway", "airport", "drop", "pickup",
    "hourly", "daily", "monthly",
    // NOTE: vehicle BRAND / model names (toyota, honda, innova, dzire, …)
    // intentionally NOT in the universal seed — see appliance-brand note
    // above. They come in via `derivedProductTokens` per account.
    // === Home services / cleaning vertical ===
    "tank", "sump", "overhead", "underground",
    "carpet", "rug",
    "curtain", "blind",
    "sanitization", "sanitisation", "disinfection", "disinfect",
    "housekeeping",
    "gardening", "landscape", "landscaping",
    "packers", "movers", "moving", "packing", "shifting", "relocation",
    "plumbing", "plumber",
    "electrical", "electrician", "wiring",
    "carpentry", "carpenter",
    "painting", "painter",
    "waterproofing", "waterproof",
    "termite", "borer", "anti",
    // === Hindi / transliterated pest & service names ===
    "khatmal", "khatmalon", "khatmalen",
    "chuha", "chuhe", "chuhon",
    "deemak", "dimak", "deemakon",
    "chipkali", "chipkalis", "chipkaliyan",
    "machhar", "machar", "machharon", "makkhar",
    "makkhi", "makhi", "makkhiyan",
    "cheenti", "cheentiyaan", "cheentiyon",
    "makdi", "makadi", "makdiyan",
    "saap", "saanp",
    "kide", "kida", "kir", "keet", "keede",
    "keeda", "keedon",
    // Hindi service words
    "dhulai", "safai", "saaf",
]);

// Backwards-compatible alias for any code still referencing the old name.
const PEST_TYPE_TOKENS = PRODUCT_TAXONOMY_TOKENS;

const SERVICE_SUFFIX_RE = /(?:pest\s+control\s+services|pest\s+control|pest\s+services|control\s+services|cleaning\s+services|extermination\s+services|fumigation\s+services|removal\s+services|inspection\s+services|treatment\s+services|repair\s+services|rental\s+services|hire\s+services|installation\s+services|maintenance\s+services|service|services|control|cleaning|extermination|fumigation|removal|treatment|inspection|repair|repairs|rental|hire|rentals|installation|maintenance|plumbing|electrician|electrical)$/i;

const looksLikeProductLineService = (term, extraTokens = null) => {
    const lower = (term || "").toLowerCase().trim();
    if (!lower) return false;
    if (!SERVICE_SUFFIX_RE.test(lower)) return false;
    const tokens = lower.split(/\s+/).filter(Boolean);
    return tokens.some(
        (t) =>
            PRODUCT_TAXONOMY_TOKENS.has(t) ||
            (extraTokens && extraTokens.has(t)),
    );
};

// Backwards-compatible alias — legacy call sites kept.
const looksLikePestTypeOrSituationService = looksLikeProductLineService;

// ---------------------------------------------------------------------------
// Locality taxonomy — Indian neighborhoods & cities where the client operates.
// Per third-round feedback: "whitefield pest control" is a Bangalore
// neighborhood, not a competitor. "[locality] + [service]" is the core
// local-intent keyword pattern — the target of the campaign, not noise.
// ---------------------------------------------------------------------------
const LOCALITY_TOKENS = new Set([
    // Bangalore / Bengaluru neighborhoods
    "whitefield", "koramangala", "indiranagar", "hsr", "btm",
    "marathahalli", "electronic", "electronics", "yelahanka", "hebbal",
    "sarjapur", "sarjapura", "sarjapore", "jayanagar", "malleshwaram",
    "rajajinagar", "basavanagudi", "bommanahalli", "banashankari",
    "mahadevapura", "kadugodi", "domlur", "frazer", "ulsoor",
    "shivajinagar", "cubbon", "yeshwanthpur", "yeshwantpur", "peenya",
    "nagavara", "rmv", "vidyaranyapura", "kengeri", "bidadi",
    "nelamangala", "ramamurthy", "kalyan", "kammanahalli", "banaswadi",
    "hoodi", "bellandur", "adugodi", "hongasandra", "bommasandra",
    "attibele", "anekal", "chandapura", "jp",
    "richmond", "brigade", "commercial", "residency",
    "wilson", "langford", "mavalli", "chamrajpet", "vv", "puram",
    "sadashivanagar", "sanjaynagar", "sanjay", "jalahalli",
    "mathikere", "gandhinagar", "seshadripuram", "chikkajala",
    "devanahalli", "budigere", "kanakapura", "bannerghatta",
    "hosur", "koramangla", "silk", "board",
    // Chennai
    "adyar", "velachery", "tambaram", "chromepet", "mylapore",
    "nungambakkam", "besant", "guindy", "porur", "anna", "egmore",
    "triplicane", "ashok", "kotturpuram",
    // Mumbai
    "andheri", "bandra", "colaba", "dadar", "thane", "borivali",
    "malad", "goregaon", "kandivali", "vashi", "juhu", "powai",
    "chembur", "kurla", "worli", "lower", "parel",
    // Hyderabad
    "hitec", "gachibowli", "kondapur", "madhapur", "banjara",
    "jubilee", "kukatpally", "secunderabad", "begumpet",
    "ameerpet", "dilsukhnagar",
    // Delhi / NCR
    "saket", "vasant", "greater", "kailash", "hauz", "khas",
    "connaught", "karol", "bagh", "rohini", "dwarka", "gurgaon",
    "noida", "faridabad", "gurugram", "ghaziabad",
    // Common Indian city names (also serve as locality when used as prefix)
    "bangalore", "bengaluru", "mumbai", "chennai", "delhi", "hyderabad",
    "pune", "kolkata", "ahmedabad", "kochi", "cochin", "jaipur",
    "lucknow", "surat", "coimbatore", "mysore", "mysuru",
    "mangalore", "mangaluru", "trivandrum", "thiruvananthapuram",
    "vizag", "visakhapatnam", "vijayawada", "nagpur", "indore",
    "bhopal", "chandigarh", "amritsar", "guwahati",
]);

const LOCALITY_SUFFIX_RE = /\b(?:nagar|layout|pura|halli|colony|village|garden|gardens|town|city|junction|circle|cross|park|estate|extension|extn)\b/i;

const looksLikeLocalityService = (term, extraLocalityTokens = null) => {
    const lower = (term || "").toLowerCase().trim();
    if (!lower) return false;
    const tokens = lower.split(/\s+/).filter(Boolean);
    const hasLocality =
        tokens.some((t) => LOCALITY_TOKENS.has(t)) ||
        (extraLocalityTokens && tokens.some((t) => extraLocalityTokens.has(t))) ||
        LOCALITY_SUFFIX_RE.test(lower);
    if (!hasLocality) return false;
    // Anchor: locality tokens are only meaningful negatives-vs-not signal
    // when paired with something service-shaped. The service anchor can be
    // (a) an explicit service suffix ("… pest control", "… repair services"),
    // (b) a product-taxonomy token anywhere in the phrase ("tempo traveller
    // mysore", "washing machine whitefield"), or (c) an account-category
    // token (handled via SERVICE_SUFFIX_RE for pest control / repair /
    // cleaning / rental etc.).
    if (SERVICE_SUFFIX_RE.test(lower)) return true;
    if (tokens.some((t) => PRODUCT_TAXONOMY_TOKENS.has(t))) return true;
    return false;
};

// ---------------------------------------------------------------------------
// Near-me / local-intent pattern. Per feedback: "near me pest control
// services" is the highest-value local-intent shape. Hard-block.
// ---------------------------------------------------------------------------
const NEAR_ME_RE = /\b(?:near\s+me|nearby|near\s+by|around\s+me|around\s+here|close\s+by|closest|in\s+my\s+area|around\s+my\s+area)\b/i;
const looksLikeNearMe = (term) => NEAR_ME_RE.test(term || "");

// ---------------------------------------------------------------------------
// Bare-category / core-market rule. Per feedback: "pest control" as a
// standalone term is the single most generic phrase in this business —
// blocking it would kill the account's core market. Same for terms whose
// non-brand tokens are ALL just generic descriptors of the same service
// ("best pest control", "top pest control", "all pest control services",
// "new pest control services", "kitchen pest control", …).
// ---------------------------------------------------------------------------
const CORE_MODIFIER_TOKENS = new Set([
    // Quality modifiers (high-intent, protect)
    "best", "top", "trusted", "good", "great", "excellent",
    "reliable", "reputable", "leading", "premier", "premium",
    "quality", "highly", "recommended", "rated", "reviewed",
    "general", "standard", "regular", "normal", "usual",
    "all", "any", "every", "some",
    // Speed / urgency
    "instant", "quick", "fast", "same", "day", "night",
    "immediate", "urgent", "emergency", "24", "hour", "hours",
    "one", "time", "monthly", "annual", "yearly", "weekly",
    "hourly", "daily",
    // Property / venue (customer describes their space)
    "home", "house", "residential", "domestic", "apartment", "flat",
    "villa", "building", "society", "housing", "co", "coop",
    "office", "commercial", "corporate", "industrial", "shop",
    "store", "restaurant", "hotel", "warehouse", "godown",
    "factory", "school", "hospital", "clinic",
    "kitchen", "bathroom", "bedroom",
    // Freshness / newness
    "new", "modern", "latest", "updated", "advanced", "modernized",
    // Generic filler / connectors / articles
    "services", "service", "solutions", "solution", "and", "or",
    "for", "with", "the", "a", "an", "of", "in", "at", "on",
    "to", "by", "from", "into", "onto",
    // Near-me anchors (also covered by NEAR_ME_RE for phrases)
    "near", "nearby", "nearest", "around", "close", "closest",
    "me", "you", "us", "my", "your", "our",
    // Booking language
    "book", "get", "find", "hire", "call", "need", "want",
    "looking", "search", "searching",
    // Extras
    "professional", "certified", "expert",  // also positive-intent
]);

// Category = the service tokens of the account name (opposite of ownBrand
// derivation). For "V Star Pest Control": {pest, control}.
const SERVICE_CATEGORY_TOKENS = new Set([
    "pest", "control", "extermination", "fumigation",
    "cleaning", "cleaners", "cleanup",
    "washing", "sanitization", "disinfection",
    "repair", "installation", "maintenance",
    "removal", "treatment", "inspection",
]);
const categoryTokensFromAccountName = (accountName) => {
    const s = (accountName || "").toLowerCase()
        .replace(/[·—\-·]/g, " ")
        .replace(/[^a-z0-9\s]/g, " ");
    const tokens = new Set(s.split(/\s+/).filter(Boolean));
    return new Set([...tokens].filter((t) => SERVICE_CATEGORY_TOKENS.has(t)));
};

const isBareCategoryOrCoreMarket = (term, ownBrandTokens, accountCategoryTokens) => {
    const lower = (term || "").toLowerCase().trim();
    if (!lower) return false;
    if (!accountCategoryTokens || accountCategoryTokens.size === 0) return false;
    const tokens = lower.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return false;
    // Must contain no client-brand tokens (else the own-brand rule handles it).
    if (tokens.some((t) => ownBrandTokens && ownBrandTokens.has(t))) return false;
    // Must contain at least one account-category token — otherwise the term
    // is not talking about the client's category at all.
    if (!tokens.some((t) => accountCategoryTokens.has(t))) return false;
    // Every other token must be a known modifier or filler. Presence of a
    // pest-type, locality, or unknown brand-like token means this is NOT
    // pure core-market — it belongs to a different rule.
    return tokens.every(
        (t) =>
            accountCategoryTokens.has(t) ||
            CORE_MODIFIER_TOKENS.has(t) ||
            /^\d+$/.test(t) // "24 hour pest control"
    );
};

// ---------------------------------------------------------------------------
// Preflight hard-block pipeline. Runs BEFORE any negative classification;
// returns a human-readable reason if the term must be protected, else null.
// Order matters — more specific / higher-signal blocks first.
//
// Universal audit rules covered (per Feb-2026 SERP-Scout Universal QA Spec):
//   #1 own-brand + bare-category
//   #2 locality + service
//   #3 near-me / local-intent
//   #4 per-account product/service taxonomy (built-in SEED + derived from
//      the current CSV's ad-group + campaign names, so vertical-specific
//      product lines auto-protect without a code change)
//   #8 compound service-attribute modifiers (chemical-free, self-drive,
//      same-day, 24-hour, on-demand, at-home, door-step, …)
// ---------------------------------------------------------------------------
const hardBlockReason = (term, opts = {}) => {
    const {
        ownBrandTokens = new Set(),
        accountCategoryTokens = new Set(),
        derivedProductTokens = null,
        derivedLocalityTokens = null,
    } = opts;
    // Unambiguous DIY / jobs / salary / how-to / tutorial patterns win
    // over every protective rule below. A search like "diy washing machine
    // repair" is legitimate low-intent even though it names a product line
    // — the customer isn't looking to hire. Own-brand still takes priority
    // over these so branded queries don't get negativized on a
    // "brand name jobs" pattern.
    if (looksLikeOwnBrand(term, ownBrandTokens)) {
        return "Client's own brand — protected from negation.";
    }
    for (const p of NEGATIVE_PATTERNS) {
        if (p.re.test(term || "")) return null; // pass through to classifier
    }
    if (hasPositiveIntent(term)) {
        return "Positive-intent / compound-service modifier (chemical-free / self-drive / same-day / professional / …) — protected from negation.";
    }
    if (looksLikeProductLineService(term, derivedProductTokens)) {
        return "Product-line service ([product/service type] + service verb) — this describes the client's actual product line, not a competitor.";
    }
    if (looksLikeLocalityService(term, derivedLocalityTokens)) {
        return "Locality-scoped query ([neighborhood/city] + service) — protected as a local-intent target, not a competitor.";
    }
    if (looksLikeNearMe(term)) {
        return "Near-me / local-intent query — the highest-value local-service keyword shape, protected.";
    }
    if (isBareCategoryOrCoreMarket(term, ownBrandTokens, accountCategoryTokens)) {
        return "Bare account-category / core-market query (e.g. \"pest control\", \"best pest control\") — protected as the business's core term.";
    }
    return null;
};

// Legacy wrapper kept for the QA-banner adjacent-pest label. Preserves the
// prior behaviour used elsewhere in the file.
const looksLikeAdjacentPestService = (term) => looksLikePestTypeOrSituationService(term || "");

const looksLikeNamedCompetitorOrDirectory = (term, ownBrandTokens) => {
    const lower = (term || "").toLowerCase();
    if (DIRECTORY_TOKENS.some((d) => lower.includes(d))) return true;
    // Heuristic: brand-shaped phrase = ends with "pest control", "cleaning",
    // "services", "enterprises" and has a leading proper-noun prefix that
    // is NOT the client's own brand AND NOT a generic descriptor. The
    // stopword check (CATEGORY_DESCRIPTOR_TOKENS) is what distinguishes
    // "hommlie pest control" (real competitor) from "chemical free pest
    // control" (customer wanting a specific method) or "bangalore pest
    // control" (generic core-market term).
    const brandSuffixes = ["pest control services", "pest control", "pest services", "enterprises", "cleaning services", "cleaners"];
    for (const suf of brandSuffixes) {
        if (lower.endsWith(" " + suf) || lower === suf) {
            const prefix = lower.slice(0, lower.length - suf.length).trim();
            const prefTokens = prefix.split(/\s+/).filter(Boolean);
            if (prefTokens.length === 0) return false;
            const anyOwn = prefTokens.some((t) => ownBrandTokens.has(t));
            if (anyOwn) return false;
            // Reject prefixes that are pure locality / pest / modifier.
            // These are handled by dedicated preflight rules — only truly
            // "brand-like" prefixes remain to trigger the competitor label.
            const brandLike = prefTokens.filter(
                (t) =>
                    !CATEGORY_DESCRIPTOR_TOKENS.has(t) &&
                    !CORE_MODIFIER_TOKENS.has(t) &&
                    !LOCALITY_TOKENS.has(t) &&
                    !PEST_TYPE_TOKENS.has(t) &&
                    t.length >= 2,
            );
            if (brandLike.length > 0) return true;
        }
    }
    return false;
};

// Derive the client's own-brand token set from the account display name.
// Strips generic service tokens so "V Star Pest Control" → {"v","star"}.
const GENERIC_ACCOUNT_TOKENS = new Set([
    "pest", "control", "services", "service", "cleaning", "cleaners",
    "solutions", "solution", "company", "co", "the", "and", "&",
    "pvt", "ltd", "llc", "inc", "limited", "private",
    "sump", "tank", "washing", "machine", "repair", "campaign",
    "fixers", "fixer", "care", "world", "point",
]);
const brandTokenSetFromAccountName = (accountName) => {
    const s = (accountName || "").toLowerCase()
        .replace(/[·—\-·]/g, " ")
        .replace(/[^a-z0-9\s]/g, " ");
    const tokens = s.split(/\s+/).filter(Boolean);
    // Keep tokens that aren't generic AND aren't purely numeric.
    const brand = tokens.filter((t) => !GENERIC_ACCOUNT_TOKENS.has(t) && !/^\d+$/.test(t));
    return new Set(brand);
};
// Does the search term look like the client's own brand? True when the
// term contains ALL non-generic brand tokens from the account name.
// Compact-substring fallback for brand tokens ≥5 chars catches "v star"
// when the account name is "Vstar" (and vice versa).
const looksLikeOwnBrand = (term, ownBrandTokens) => {
    if (!ownBrandTokens || ownBrandTokens.size === 0) return false;
    const raw = (term || "").toLowerCase();
    const compact = raw.replace(/[^a-z0-9]/g, "");
    for (const bt of ownBrandTokens) {
        if (bt.length < 2) continue;
        const btEsc = bt.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const re = new RegExp(`(^|[^a-z0-9])${btEsc}($|[^a-z0-9])`, "i");
        if (re.test(raw)) continue;
        // Fallback: for distinctive 5+ char brand tokens, allow a compact
        // substring match ("Vstar" account → "v star" search term).
        if (bt.length >= 5 && compact.includes(bt)) continue;
        return false;
    }
    return true;
};

// Return the array of negative-signal reasons for an AGGREGATED row.
// - `row`         — aggregated row with totals across match types / ad groups / days
// - `minSpend`    — currency-agnostic minimum spend (in account currency) below
//                   which a zero-conversion signal is considered too thin
// - `minClicks`   — minimum click count for a zero-conversion signal to qualify
// - `currency`    — account currency code (INR, GBP, USD, ...) for reason text
// - `ownBrandTokens` — Set of client brand tokens to protect from negation
const negativeReasonFor = (row, opts = {}) => {
    const {
        minSpend = 0,
        minClicks = 3,
        currency = "",
        ownBrandTokens = new Set(),
    } = opts;
    const reasons = [];
    // #2 hard-block: never suggest excluding the client's own brand.
    if (looksLikeOwnBrand(row.term, ownBrandTokens)) return [];
    // Hard-block: positive-intent modifiers (chemical-free, herbal, organic,
    // professional, certified, ...). Customers using these words are
    // signalling a specific service preference or purchase intent — the
    // exact opposite of a low-value query. Blocking here is the fix for
    // the Feb-2026 audit bug where "chemical free pest control", "natural
    // pest control services", "professional cockroach pest control", etc.
    // were being suggested as negatives.
    if (hasPositiveIntent(row.term)) return [];
    // Hard-block: adjacent pest-service terms (mosquito / lizard / spider
    // / silverfish / termite / …). The auditor cannot know whether the
    // client services these pest types, so it must not auto-negativize.
    // The parent aggregator surfaces these in the QA banner so the
    // operator can confirm business scope before excluding.
    if (looksLikeAdjacentPestService(row.term)) return [];
    // Semantic "free" — skip the compound-modifier case ("chemical-free",
    // "insect free", "bug free", "inspection free", …).
    if (/\bfree\b/i.test(row.term) && !isCompoundFreeModifier(row.term)) {
        reasons.push('contains "free"');
    }
    for (const p of NEGATIVE_PATTERNS) {
        if (p.re.test(row.term)) reasons.push(`contains "${p.label}"`);
    }
    // Named competitor / directory — much higher confidence than generic
    // category terms. The tier is set on the row itself in the aggregator.
    if (looksLikeNamedCompetitorOrDirectory(row.term, ownBrandTokens)) {
        reasons.push("named competitor / directory");
    }
    // Zero-conversion spend signal — only counts as evidence past the
    // minimum click AND minimum spend thresholds (aggregated totals).
    if (row.conversions === 0 && row.cost >= minSpend && row.clicks >= minClicks) {
        reasons.push(`${formatMoney(row.cost, currency)} spent · 0 conv`);
    }
    return reasons;
};

const isAlreadyExcluded = (statusRaw) => {
    const s = normalizeHeader(statusRaw);
    return s.includes("excluded") || s.includes("negative") || s.includes("negativ");
};

const isAlreadyAdded = (statusRaw) => {
    const s = normalizeHeader(statusRaw);
    if (!s) return false;
    return (
        s.includes("added") ||
        s.includes("keyword") ||
        s === "exact" ||
        s === "phrase" ||
        s === "broad"
    );
};

/* ------------------------------------------------------------------------- */
/* Main component                                                             */
/* ------------------------------------------------------------------------- */

export const SearchTermsAuditor = ({ preloadedAudit, onAuditSaved }) => {
    const [rawRows, setRawRows] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [filename, setFilename] = useState("");
    const [ctrThreshold, setCtrThreshold] = useState(5); // %
    const [negLevel, setNegLevel] = useState("campaign"); // "campaign" | "ad_group"
    const [matchType, setMatchType] = useState("Phrase"); // "Phrase" | "Exact" | "Broad"
    const [selectedNeg, setSelectedNeg] = useState({}); // {term:true}
    const [selectedKw, setSelectedKw] = useState({});   // {term:true}
    // Manual Review mode (new): unified table with AI relevance score.
    const [viewMode, setViewMode] = useState("manual"); // "manual" | "auto"
    const [aiScores, setAiScores] = useState({}); // {termLowerCase: {relevance_score, is_candidate_junk, reason}}
    const [aiScoring, setAiScoring] = useState(false);
    const [aiSummary, setAiSummary] = useState(null);
    const [businessContext, setBusinessContext] = useState("");
    // Per-row actions: { [term]: { action: "negative"|"keyword_exact"|"keyword_phrase"|"exact_low_bid" } }
    const [rowActions, setRowActions] = useState({});
    const [baselineCpc, setBaselineCpc] = useState(0.5);
    const [syncing, setSyncing] = useState(false);
    const [processedNorms, setProcessedNorms] = useState([]);
    const [showProcessed, setShowProcessed] = useState(false);
    const [currentCustomerId, setCurrentCustomerId] = useState("");
    const [currentAccountName, setCurrentAccountName] = useState("");
    // Account currency code (INR / GBP / USD / …). Populated from the audit
    // meta so every money figure — reasons, PDF, "Baseline CPC" label —
    // reflects the actual account currency instead of a placeholder symbol.
    const [accountCurrency, setAccountCurrency] = useState("");
    // Blocked term surface: any term that appears in the QA gate below is
    // recorded so we can render an in-app warning explaining why an
    // otherwise-flagged row was removed from the negatives list.
    const [qaWarnings, setQaWarnings] = useState([]);
    const fileInputRef = useRef(null);

    const handleFile = (file) => {
        if (!file) return;
        const lowerName = (file.name || "").toLowerCase();
        if (!lowerName.endsWith(".csv") && !file.type.includes("csv")) {
            toast.error(
                `That looks like a ${lowerName.split(".").pop() || "non-CSV"} file. Please export your Search terms report as CSV from Google Ads (Reports → Download → CSV).`,
                { duration: 8000 }
            );
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const text = ev.target.result;
                const grid = parseCsv(text).filter((r) => r.some((c) => c && c.trim()));
                if (!grid.length) {
                    toast.error("CSV appears empty");
                    return;
                }
                // Google Ads exports often prefix with a title row; find the header row heuristically.
                let headerIdx = 0;
                for (let i = 0; i < Math.min(grid.length, 5); i++) {
                    if (findHeaderIndex(grid[i], "search_term") !== -1) {
                        headerIdx = i;
                        break;
                    }
                }
                const hdrs = grid[headerIdx];
                const body = grid.slice(headerIdx + 1);
                if (findHeaderIndex(hdrs, "search_term") === -1) {
                    toast.error("No 'Search term' column found — check the CSV format.");
                    return;
                }
                setHeaders(hdrs);
                setRawRows(body);
                setFilename(file.name);
                setSelectedNeg({});
                setSelectedKw({});
                // CSV uploads don't carry account metadata — clear any
                // stale currency from a prior audit so the UI shows raw
                // numbers instead of the wrong symbol.
                setAccountCurrency("");
                setCurrentAccountName("");
                toast.success(`Loaded ${body.length} rows from ${file.name}`);
            } catch (e) {
                toast.error("Failed to parse CSV: " + (e.message || "unknown"));
            }
        };
        reader.onerror = () => toast.error("Could not read file");
        reader.readAsText(file);
    };

    const normalized = useMemo(() => {
        if (!rawRows.length || !headers.length) return [];
        const termIdx = findHeaderIndex(headers, "search_term");
        const statusIdx = findHeaderIndex(headers, "added_excluded");
        const campIdx = findHeaderIndex(headers, "campaign");
        const agIdx = findHeaderIndex(headers, "ad_group");
        const impIdx = findHeaderIndex(headers, "impressions");
        const clkIdx = findHeaderIndex(headers, "clicks");
        const costIdx = findHeaderIndex(headers, "cost");
        const ctrIdx = findHeaderIndex(headers, "ctr");
        const convIdx = findHeaderIndex(headers, "conversions");
        const cpaIdx = findHeaderIndex(headers, "cost_per_conv");

        return rawRows.map((r) => {
            const term = (r[termIdx] || "").trim();
            const clicks = parseNumber(r[clkIdx]);
            const impressions = parseNumber(r[impIdx]);
            const cost = parseNumber(r[costIdx]);
            const rawCtr = ctrIdx !== -1 ? parseNumber(r[ctrIdx]) : (impressions > 0 ? (clicks / impressions) * 100 : 0);
            const conversions = parseNumber(r[convIdx]);
            // Prefer explicit CPA column; fall back to cost/conv computation.
            const cpaFromCsv = cpaIdx !== -1 ? parseNumber(r[cpaIdx]) : 0;
            const cost_per_conv =
                cpaFromCsv > 0
                    ? cpaFromCsv
                    : conversions > 0
                    ? cost / conversions
                    : null;
            const status = statusIdx !== -1 ? (r[statusIdx] || "").trim() : "";
            return {
                term,
                status,
                campaign: campIdx !== -1 ? (r[campIdx] || "").trim() : "",
                ad_group: agIdx !== -1 ? (r[agIdx] || "").trim() : "",
                impressions,
                clicks,
                cost,
                ctr: rawCtr,
                conversions,
                cost_per_conv,
                excluded: isAlreadyExcluded(status),
                added: isAlreadyAdded(status),
            };
        }).filter((row) => row.term);
    }, [rawRows, headers]);

    // Client's own brand tokens, derived once per account. Used to keep
    // brand-name queries out of the negatives pool no matter what the
    // performance numbers say (fix #2 in the report).
    const ownBrandTokens = useMemo(
        () => brandTokenSetFromAccountName(currentAccountName),
        [currentAccountName],
    );
    // Client's service-category tokens ("pest", "control" for a pest control
    // account). Used by the bare-category preflight guard to protect the
    // account's own category name and its trivial variants (kitchen pest
    // control, best pest control, all pest control services, …) from being
    // flagged as negatives. Falls back to an empty set when the account
    // name has no recognisable service token — the bare-category guard
    // then no-ops, which is the safe default for unknown verticals.
    const accountCategoryTokens = useMemo(
        () => categoryTokensFromAccountName(currentAccountName),
        [currentAccountName],
    );
    // Per-account product / locality vocabulary derived from the CSV's own
    // campaign + ad-group names. Rule #4 of the Universal QA Spec: pull the
    // vertical-specific taxonomy from the account's own existing keyword
    // list & ad group structure — so a client whose ad groups are named
    // "Bed Bug", "Cockroach Control", "Tempo Traveller Mysore",
    // "Washing Machine Repair" automatically gets its own product lines
    // protected without a code change. Also captures locality names when
    // agencies name campaigns "Bengaluru - Search" / "Whitefield Local" etc.
    const { derivedProductTokens, derivedLocalityTokens } = useMemo(() => {
        const products = new Set();
        const localities = new Set();
        if (!normalized.length) return { derivedProductTokens: products, derivedLocalityTokens: localities };
        const seenNames = new Set();
        for (const r of normalized) {
            for (const name of [r.campaign, r.ad_group]) {
                const n = (name || "").trim().toLowerCase();
                if (!n || seenNames.has(n)) continue;
                seenNames.add(n);
                const toks = n.split(/[\s\-_/|]+/).filter(Boolean);
                for (const t of toks) {
                    if (t.length < 3) continue;
                    if (/^\d+$/.test(t)) continue;
                    if (ownBrandTokens.has(t)) continue;
                    if (accountCategoryTokens.has(t)) continue;
                    if (CORE_MODIFIER_TOKENS.has(t)) continue;
                    if (CATEGORY_DESCRIPTOR_TOKENS.has(t)) continue;
                    // Locality-shaped tokens (known city/neighborhood, or
                    // ending in a locality suffix like "nagar" / "layout").
                    if (LOCALITY_TOKENS.has(t) || LOCALITY_SUFFIX_RE.test(t)) {
                        localities.add(t);
                    } else {
                        // Every other distinctive ad-group / campaign token
                        // is treated as part of the account's product-line
                        // vocabulary (bed, bug, cockroach, tempo, van,
                        // washing, machine, ac, geyser, tank, sump, …).
                        products.add(t);
                    }
                }
            }
        }
        return { derivedProductTokens: products, derivedLocalityTokens: localities };
    }, [normalized, ownBrandTokens, accountCategoryTokens]);

    // ---- FIX #1: aggregate before classifying ----
    // The Google Ads search-terms export produces ONE ROW per (term,
    // match_type, campaign, ad_group). Classifying those rows in isolation
    // was the root cause of the 30-day audit bug — a phrase converting in
    // one ad group while showing zero conversions in another was flagged
    // as both a suggested negative AND a high-signal keyword.
    //
    // Group all occurrences of the same normalized term into a single
    // aggregated row (sum impressions / clicks / cost / conversions,
    // recompute CTR + CPA on the totals, retain a representative campaign
    // + ad_group by picking the highest-spend slice).
    const aggregated = useMemo(() => {
        if (!normalized.length) return [];
        const bucket = new Map();
        for (const r of normalized) {
            const key = (r.term || "").trim().toLowerCase();
            if (!key) continue;
            let agg = bucket.get(key);
            if (!agg) {
                agg = {
                    term: r.term.trim(),
                    key,
                    campaign: r.campaign,
                    ad_group: r.ad_group,
                    _topSpendSlice: r.cost || 0,
                    impressions: 0,
                    clicks: 0,
                    cost: 0,
                    conversions: 0,
                    excludedCount: 0,
                    addedCount: 0,
                    sliceCount: 0,
                    campaigns: new Set(),
                    ad_groups: new Set(),
                };
                bucket.set(key, agg);
            }
            agg.impressions += r.impressions || 0;
            agg.clicks += r.clicks || 0;
            agg.cost += r.cost || 0;
            agg.conversions += r.conversions || 0;
            agg.sliceCount += 1;
            if (r.excluded) agg.excludedCount += 1;
            if (r.added) agg.addedCount += 1;
            if (r.campaign) agg.campaigns.add(r.campaign);
            if (r.ad_group) agg.ad_groups.add(r.ad_group);
            // Keep the campaign/ad_group of the highest-spend slice as the
            // "primary" for export purposes.
            if ((r.cost || 0) > agg._topSpendSlice) {
                agg._topSpendSlice = r.cost;
                agg.campaign = r.campaign;
                agg.ad_group = r.ad_group;
            }
        }
        return Array.from(bucket.values()).map((a) => ({
            term: a.term,
            campaign: a.campaign,
            ad_group: a.ad_group,
            impressions: a.impressions,
            clicks: a.clicks,
            cost: Math.round(a.cost * 100) / 100,
            conversions: Math.round(a.conversions * 100) / 100,
            ctr: a.impressions > 0 ? (a.clicks / a.impressions) * 100 : 0,
            cost_per_conv: a.conversions > 0 ? a.cost / a.conversions : null,
            // If ANY slice has already been excluded or added, respect it —
            // Google Ads will noop the mutation anyway.
            excluded: a.excludedCount > 0,
            added: a.addedCount > 0,
            slice_count: a.sliceCount,
            campaign_count: a.campaigns.size,
            ad_group_count: a.ad_groups.size,
        }));
    }, [normalized]);

    // ---- FIX #4: minimum sample-size threshold ----
    // Compute the account's blended CPA from the aggregated data and set a
    // spend floor at 2× that CPA before a zero-conversion term qualifies as
    // "evidence of waste". Falls back to a reasonable default when the
    // account is too fresh to have a meaningful CPA.
    const spendThresholds = useMemo(() => {
        const totalCost = aggregated.reduce((s, r) => s + (r.cost || 0), 0);
        const totalConv = aggregated.reduce((s, r) => s + (r.conversions || 0), 0);
        const cpa = totalConv > 0 ? totalCost / totalConv : 0;
        // 2× the blended CPA is the "give it a fair chance" line; below
        // that, a 0-conv term is an unformed sample.
        const minSpend = cpa > 0 ? Math.max(1, cpa * 2) : 0;
        return { blendedCpa: cpa, minSpend, minClicks: 3 };
    }, [aggregated]);

    const rawNegatives = useMemo(() => {
        const out = [];
        for (const row of aggregated) {
            if (row.excluded) continue;
            // Preflight hard-block pipeline. Runs BEFORE any classification
            // so terms matching own-brand / positive-intent / product-line /
            // locality / near-me / bare-category patterns can NEVER enter
            // the negatives list, regardless of spend or evidence rules.
            // Blocked terms surface in the QA banner via the separate
            // QA-warnings useMemo below.
            if (hardBlockReason(row.term, {
                ownBrandTokens,
                accountCategoryTokens,
                derivedProductTokens,
                derivedLocalityTokens,
            })) continue;
            const reasons = negativeReasonFor(row, {
                minSpend: spendThresholds.minSpend,
                minClicks: spendThresholds.minClicks,
                currency: accountCurrency,
                ownBrandTokens,
            });
            if (!reasons.length) continue;
            // Confidence tier — separates named-competitor cases (high) from
            // generic category terms (low, warn the user before applying).
            const namedCompetitor = reasons.some((r) => r === "named competitor / directory");
            const patternMatch = reasons.some((r) => r.startsWith('contains "'));
            const tier = namedCompetitor
                ? "high"
                : patternMatch
                ? "medium"
                : "low"; // spend-based only → weakest signal
            out.push({ ...row, reasons, tier });
        }
        out.sort((a, b) => {
            // High tier first, then by wasted spend desc.
            const rank = { high: 0, medium: 1, low: 2 };
            const t = rank[a.tier] - rank[b.tier];
            return t !== 0 ? t : b.cost - a.cost;
        });
        return out;
    }, [aggregated, accountCurrency, ownBrandTokens, accountCategoryTokens, derivedProductTokens, derivedLocalityTokens, spendThresholds]);

    // ---- FIX #3 + #8: mutual-exclusion + QA gate ----
    // Build the negative-term set first so newKeywords can guarantee no
    // overlap, and drop any negative whose aggregated conversions > 0 even
    // if the pattern rule fired (belt-and-suspenders against the exact bug
    // in the 30-day audit).
    const { negatives, newKeywords, qaGateWarnings } = useMemo(() => {
        const warnings = [];
        const convertingTermsLower = new Set(
            aggregated
                .filter((r) => (r.conversions || 0) > 0)
                .map((r) => r.term.trim().toLowerCase()),
        );
        // Surface every preflight-blocked term with its specific reason.
        // Operators need to see WHY these were skipped so they can confirm
        // business scope (adjacent pest / locality), celebrate high-value
        // intent (chemical-free / professional), or spot core-market
        // false-positives that made it into the raw candidate pool.
        // Own-brand hits are silent — they'd otherwise flood the banner on
        // every large account.
        const blockOpts = {
            ownBrandTokens,
            accountCategoryTokens,
            derivedProductTokens,
            derivedLocalityTokens,
        };
        for (const row of aggregated) {
            if (row.excluded) continue;
            const reason = hardBlockReason(row.term, blockOpts);
            if (!reason) continue;
            if (reason.startsWith("Client's own brand")) continue;
            warnings.push({ term: row.term, reason });
        }
        const safeNegs = [];
        for (const n of rawNegatives) {
            const key = n.term.trim().toLowerCase();
            if (convertingTermsLower.has(key)) {
                warnings.push({
                    term: n.term,
                    reason: `Converted ${n.conversions} time(s) in this window — removed from negatives.`,
                });
                continue;
            }
            // Belt-and-suspenders: preflight already blocked own-brand,
            // but assert here so a future refactor can't regress it.
            const block = hardBlockReason(n.term, blockOpts);
            if (block) {
                warnings.push({ term: n.term, reason: block });
                continue;
            }
            safeNegs.push(n);
        }
        // High-signal keywords, computed from the SAME aggregated set so
        // both outputs share one source of truth.
        const negSet = new Set(safeNegs.map((n) => n.term.trim().toLowerCase()));
        const kw = [];
        const seen = new Set();
        for (const row of aggregated) {
            if (row.added || row.excluded) continue;
            const key = row.term.trim().toLowerCase();
            if (seen.has(key)) continue;
            if (negSet.has(key)) continue; // mutual-exclusion invariant
            const isHighCtr = row.ctr >= ctrThreshold && row.clicks >= 1;
            const isConverting = (row.conversions || 0) >= 1;
            if (!isHighCtr && !isConverting) continue;
            const reasons = [];
            if (isConverting) reasons.push(`${row.conversions} conv`);
            if (isHighCtr) reasons.push(`CTR ${row.ctr.toFixed(1)}%`);
            seen.add(key);
            kw.push({ ...row, reasons });
        }
        kw.sort((a, b) => (b.conversions - a.conversions) || (b.ctr - a.ctr));
        return { negatives: safeNegs, newKeywords: kw, qaGateWarnings: warnings };
    }, [aggregated, rawNegatives, ctrThreshold, ownBrandTokens, accountCategoryTokens, derivedProductTokens, derivedLocalityTokens]);

    // Publish QA warnings for the banner (out-of-render effect).
    useEffect(() => {
        setQaWarnings(qaGateWarnings);
    }, [qaGateWarnings]);

    const toggleAllNeg = (checked) => {
        const next = {};
        if (checked) negatives.forEach((n) => (next[n.term] = true));
        setSelectedNeg(next);
    };

    const toggleAllKw = (checked) => {
        const next = {};
        if (checked) newKeywords.forEach((k) => (next[k.term] = true));
        setSelectedKw(next);
    };

    // Auto-select all when new rows load (intentionally keyed on rawRows only so filter tweaks don't override user choices).
    useEffect(() => {
        if (!negatives.length && !newKeywords.length) return;
        setSelectedNeg(Object.fromEntries(negatives.map((n) => [n.term, true])));
        setSelectedKw(Object.fromEntries(newKeywords.map((k) => [k.term, true])));
        // Reset manual-review state too so the AI has to be re-run per upload.
        setRowActions({});
        setAiScores({});
        setAiSummary(null);
    }, [rawRows]);

    const negCount = Object.values(selectedNeg).filter(Boolean).length;
    const kwCount = Object.values(selectedKw).filter(Boolean).length;

    const handleExportNegatives = () => {
        const picked = negatives.filter((n) => selectedNeg[n.term]);
        if (!picked.length) {
            toast.error("Select at least one negative to export");
            return;
        }
        // Google Ads Editor negative keyword list import schema.
        const isAdGroup = negLevel === "ad_group";
        const rows = [
            isAdGroup
                ? ["Campaign", "Ad group", "Keyword", "Match type", "Status"]
                : ["Campaign", "Keyword", "Match type", "Status"],
        ];
        for (const n of picked) {
            const kw = n.term;
            const camp = n.campaign || "";
            if (isAdGroup) {
                rows.push([camp, n.ad_group || "", kw, "Negative Phrase", "Enabled"]);
            } else {
                rows.push([camp, kw, "Negative Phrase", "Enabled"]);
            }
        }
        const fname = `negatives_${negLevel}_${Date.now()}.csv`;
        downloadCsv(fname, toCsv(rows));
        toast.success(`Exported ${picked.length} negatives · ${negLevel === "ad_group" ? "ad-group" : "campaign"} level`);
    };

    // ---- Manual Review mode: AI scoring + Add to negatives ----
    const handleAiScore = async () => {
        if (!normalized.length) {
            toast.error("Upload a CSV or fetch from Google Ads first");
            return;
        }
        setAiScoring(true);
        const t = toast.loading(`Claude scoring ${normalized.length} search terms…`);
        try {
            const payload = {
                terms: normalized.map((r) => ({
                    term: r.term,
                    clicks: r.clicks || 0,
                    cost: r.cost || 0,
                    conversions: r.conversions || 0,
                })),
                business_context: businessContext,
            };
            const data = await adsSearchTermsScore(payload);
            const map = {};
            for (const s of data.scores || []) {
                if (s.term) map[s.term.toLowerCase()] = s;
            }
            setAiScores(map);
            setAiSummary(data.summary || null);
            // Pre-assign every AI-flagged junk row to "negative" so a happy
            // user is one click from Sync.
            setRowActions((prev) => {
                const next = { ...prev };
                for (const r of normalized) {
                    const s = map[r.term.toLowerCase()];
                    if (s?.is_candidate_junk && !next[r.term]) {
                        next[r.term] = { action: "negative" };
                    }
                }
                return next;
            });
            toast.success(
                `Scored ${data.summary?.total_in || normalized.length} terms · ${data.summary?.junk_flagged || 0} flagged as junk`,
                { id: t }
            );
        } catch (e) {
            const msg = errText(e, "Scoring failed");
            toast.error(msg, { id: t });
        } finally {
            setAiScoring(false);
        }
    };

    const handleAddManualToNegatives = () => {
        const picked = normalized.filter((r) => rowActions[r.term]?.action === "negative");
        if (!picked.length) {
            toast.error("Assign 'Negative' to at least one row first");
            return;
        }
        const isAdGroup = negLevel === "ad_group";
        const rows = [
            isAdGroup
                ? ["Campaign", "Ad group", "Keyword", "Match type", "Status"]
                : ["Campaign", "Keyword", "Match type", "Status"],
        ];
        for (const n of picked) {
            const kw = n.term;
            const camp = n.campaign || "";
            if (isAdGroup) {
                rows.push([camp, n.ad_group || "", kw, "Negative Phrase", "Enabled"]);
            } else {
                rows.push([camp, kw, "Negative Phrase", "Enabled"]);
            }
        }
        const fname = `manual_negatives_${negLevel}_${Date.now()}.csv`;
        downloadCsv(fname, toCsv(rows));
        toast.success(
            `Exported ${picked.length} negative${picked.length === 1 ? "" : "s"} · ${negLevel === "ad_group" ? "ad-group" : "campaign"} level`
        );
    };

    // Sync all assigned per-row actions to Google Ads (or Editor CSV fallback).
    const handleSyncActions = async () => {
        const actionRows = normalized.filter((r) => rowActions[r.term]?.action);
        if (!actionRows.length) {
            toast.error("Assign at least one action before syncing");
            return;
        }
        setSyncing(true);
        const t = toast.loading(`Syncing ${actionRows.length} action${actionRows.length === 1 ? "" : "s"} to Google Ads…`);
        try {
            const payload = {
                customer_id: currentCustomerId || "",
                account_name: currentAccountName || "",
                audit_id: preloadedAudit?.audit?.id || null,
                neg_level: negLevel,
                baseline_cpc: Number(baselineCpc) || 0.5,
                low_bid_ratio: 0.5,
                actions: actionRows.map((r) => ({
                    term: r.term,
                    action: rowActions[r.term].action,
                    campaign: r.campaign || "",
                    ad_group: r.ad_group || "",
                    match_type: rowActions[r.term].matchType || null,
                    cpc_bid: r.cpc || null,
                })),
            };
            const data = await adsSearchTermsSync(payload);
            // Download whichever CSVs came back.
            const stamp = new Date().toISOString().replace(/[:.]/g, "-");
            if (data.csvs?.negatives) {
                downloadCsv(`STA-Negatives-${negLevel}-${stamp}.csv`, data.csvs.negatives);
            }
            if (data.csvs?.keywords) {
                downloadCsv(`STA-Keywords-${stamp}.csv`, data.csvs.keywords);
            }
            if (data.csvs?.low_bid_keywords) {
                downloadCsv(`STA-LowBid-Keywords-${stamp}.csv`, data.csvs.low_bid_keywords);
            }
            // Merge just-synced terms into the processed set so they hide.
            const newNorms = actionRows.map((r) => r.term.trim().toLowerCase().replace(/\s+/g, " "));
            setProcessedNorms((prev) => Array.from(new Set([...(prev || []), ...newNorms])));
            const c = data.counts || {};
            toast.success(
                `Synced · ${c.negative || 0} neg · ${(c.keyword_exact || 0) + (c.keyword_phrase || 0)} kw · ${c.exact_low_bid || 0} low-bid — Editor CSVs downloaded`,
                { id: t }
            );
        } catch (e) {
            const msg = errText(e, "Sync failed");
            toast.error(msg, { id: t });
        } finally {
            setSyncing(false);
        }
    };

    const handleExportNewKeywords = () => {
        const picked = newKeywords.filter((k) => selectedKw[k.term]);
        if (!picked.length) {
            toast.error("Select at least one keyword to export");
            return;
        }
        const rows = [["Campaign", "Ad group", "Keyword", "Match type", "Status"]];
        for (const k of picked) {
            rows.push([
                k.campaign || "",
                k.ad_group || "",
                k.term,
                matchType,
                "Enabled",
            ]);
        }
        const fname = `new_keywords_${matchType.toLowerCase()}_${Date.now()}.csv`;
        downloadCsv(fname, toCsv(rows));
        toast.success(`Exported ${picked.length} keywords · ${matchType} match`);
    };

    const clearAll = () => {
        setRawRows([]);
        setHeaders([]);
        setFilename("");
        setSelectedNeg({});
        setSelectedKw({});
    };

    // Called by AdsImportBar — synthesize a "CSV" shape from the API rows so the
    // existing header-indexed parser + downstream tables/PDF work unchanged.
    const hydrateFromAdsRows = (rows, filenameLabel) => {
        const synthHeaders = [
            "Campaign",
            "Ad group",
            "Search term",
            "Match type",
            "Added/Excluded",
            "Clicks",
            "Impressions",
            "CTR",
            "Cost",
            "Conversions",
            "Cost / conv.",
        ];
        const body = (rows || []).map((r) => [
            r.campaign || "",
            r.ad_group || "",
            r.search_term || "",
            r.match_type || "",
            "None",
            String(r.clicks ?? 0),
            String(r.impressions ?? 0),
            `${(r.ctr ?? 0).toFixed(2)}%`,
            (r.cost ?? 0).toFixed(2),
            String(r.conversions ?? 0),
            r.cost_per_conv != null ? r.cost_per_conv.toFixed(2) : "0",
        ]);
        setHeaders(synthHeaders);
        setRawRows(body);
        setFilename(filenameLabel);
        setSelectedNeg({});
        setSelectedKw({});
    };

    const handleFetchFromAds = async (customerId, dateRange, account, campaignIds = []) => {
        const data = await adsSearchTerms(customerId, dateRange, account?.name || "", campaignIds);
        const rows = data?.rows || [];
        const name = account?.name ? account.name : `Account ${customerId}`;
        const scope = campaignIds && campaignIds.length
            ? ` · ${campaignIds.length} campaign${campaignIds.length === 1 ? "" : "s"}`
            : "";
        setCurrentCustomerId(customerId);
        setCurrentAccountName(account?.name || "");
        // Pull currency from the account object (populated by AdsImportBar via
        // /api/ads/accounts) with fallback to the backend response.
        setAccountCurrency(account?.currency || data?.currency || "");
        hydrateFromAdsRows(
            rows,
            `${name}${scope} · ${dateRange.replace(/_/g, " ").toLowerCase()}`
        );
        // Load already-processed terms for this account so they get hidden.
        try {
            const p = await adsSearchTermsProcessed(customerId);
            setProcessedNorms(p.processed_norms || []);
        } catch {
            setProcessedNorms([]);
        }
        onAuditSaved?.();
    };

    // When opened from sidebar history, load the full audit and hydrate.
    useEffect(() => {
        if (!preloadedAudit?.audit?.id) return;
        (async () => {
            try {
                const full = await adsAuditGet(preloadedAudit.audit.id);
                hydrateFromAdsRows(full.rows || [], full.label || `Audit ${full.id}`);
                setCurrentCustomerId(full.customer_id || "");
                setCurrentAccountName(full.account_name || "");
                setAccountCurrency(full.currency || full.account_currency || "");
                if (full.customer_id) {
                    try {
                        const p = await adsSearchTermsProcessed(full.customer_id);
                        setProcessedNorms(p.processed_norms || []);
                    } catch {
                        setProcessedNorms([]);
                    }
                }
            } catch (e) {
                toast.error("Could not load audit");
            }
        })();
    }, [preloadedAudit?.key]);

    const handleExportPdf = () => {
        if (!normalized.length) {
            toast.error("Upload a CSV first");
            return;
        }
        const doc = new jsPDF({ unit: "pt", format: "letter" });
        const marginX = 40;
        let y = 48;

        // Header
        doc.setFont("helvetica", "bold");
        doc.setFontSize(20);
        doc.setTextColor(10, 10, 10);
        doc.text("Search Terms Audit", marginX, y);
        y += 8;
        doc.setDrawColor(0, 47, 167); // klein blue
        doc.setLineWidth(3);
        doc.line(marginX, y, marginX + 120, y);
        y += 22;

        // Metadata block
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        const now = new Date();
        doc.text(`Source: ${filename || "uploaded CSV"}`, marginX, y);
        y += 14;
        doc.text(
            `Generated: ${now.toLocaleString()} · ${normalized.length} terms scanned · CTR threshold ${ctrThreshold}%`,
            marginX,
            y
        );
        y += 20;

        // Totals bar with blended CPA
        const totalWaste = negatives.reduce((s, n) => s + n.cost, 0);
        const totalWin = newKeywords.reduce((s, k) => s + k.conversions, 0);
        const totalSpend = normalized.reduce((s, r) => s + r.cost, 0);
        const totalConv = normalized.reduce((s, r) => s + r.conversions, 0);
        const blendedCpa = totalConv > 0 ? totalSpend / totalConv : null;

        doc.setFillColor(10, 10, 10);
        doc.rect(marginX, y, 515, 58, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        // Row 1 (waste + wins)
        doc.text("SUGGESTED NEGATIVES", marginX + 12, y + 14);
        doc.text("HIGH-SIGNAL KEYWORDS", marginX + 275, y + 14);
        doc.setFontSize(13);
        doc.text(`${negatives.length}  ·  ${totalWaste.toFixed(2)} at risk`, marginX + 12, y + 28);
        doc.text(`${newKeywords.length}  ·  ${totalWin.toFixed(1)} total conv.`, marginX + 275, y + 28);
        // Row 2 (blended)
        doc.setFontSize(8);
        doc.setTextColor(180, 180, 180);
        doc.text("TOTAL SPEND", marginX + 12, y + 44);
        doc.text("TOTAL CONV.", marginX + 175, y + 44);
        doc.text("BLENDED CPA", marginX + 338, y + 44);
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.text(totalSpend.toFixed(2), marginX + 12, y + 54);
        doc.text(totalConv.toFixed(1), marginX + 175, y + 54);
        // Highlight CPA in klein-blue for the "hero" number
        doc.setTextColor(140, 190, 255);
        doc.text(
            blendedCpa !== null ? blendedCpa.toFixed(2) : "—",
            marginX + 338,
            y + 54
        );
        y += 74;

        // Negatives table
        doc.setTextColor(10, 10, 10);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("Suggested Negatives", marginX, y);
        y += 8;

        const currencyLabel = currencySymbol(accountCurrency) || accountCurrency || "";
        const costHeader = currencyLabel ? `Cost (${currencyLabel})` : "Cost";
        const cpaHeader = currencyLabel ? `Cost/Conv (${currencyLabel})` : "Cost/Conv";
        autoTable(doc, {
            startY: y,
            margin: { left: marginX, right: marginX },
            head: [["Search Term", "Clicks", costHeader, "CTR %", "Conv.", cpaHeader, "Reason"]],
            body: negatives.map((n) => [
                n.term,
                n.clicks,
                n.cost.toFixed(2),
                n.ctr.toFixed(1),
                n.conversions,
                n.cost_per_conv !== null && n.cost_per_conv !== undefined ? n.cost_per_conv.toFixed(2) : "—",
                n.reasons.join(" · "),
            ]),
            styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
            headStyles: { fillColor: [220, 38, 38], textColor: 255, fontStyle: "bold" },
            alternateRowStyles: { fillColor: [250, 250, 250] },
            columnStyles: {
                0: { cellWidth: 130 },
                1: { cellWidth: 36, halign: "right" },
                2: { cellWidth: 44, halign: "right" },
                3: { cellWidth: 38, halign: "right" },
                4: { cellWidth: 36, halign: "right" },
                5: { cellWidth: 52, halign: "right" },
                6: { cellWidth: "auto", textColor: [180, 40, 40] },
            },
        });

        y = doc.lastAutoTable.finalY + 24;

        // Force new page if we're too low
        if (y > 680) {
            doc.addPage();
            y = 48;
        }

        // New keywords table
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("Suggested New Keywords", marginX, y);
        y += 8;

        autoTable(doc, {
            startY: y,
            margin: { left: marginX, right: marginX },
            head: [["Search Term", "Clicks", costHeader, "CTR %", "Conv.", cpaHeader, "Signal"]],
            body: newKeywords.map((k) => [
                k.term,
                k.clicks,
                k.cost.toFixed(2),
                k.ctr.toFixed(1),
                k.conversions,
                k.cost_per_conv !== null && k.cost_per_conv !== undefined ? k.cost_per_conv.toFixed(2) : "—",
                k.reasons.join(" · "),
            ]),
            styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
            headStyles: { fillColor: [0, 47, 167], textColor: 255, fontStyle: "bold" },
            alternateRowStyles: { fillColor: [250, 250, 250] },
            columnStyles: {
                0: { cellWidth: 130 },
                1: { cellWidth: 36, halign: "right" },
                2: { cellWidth: 44, halign: "right" },
                3: { cellWidth: 38, halign: "right" },
                4: { cellWidth: 36, halign: "right" },
                5: { cellWidth: 52, halign: "right" },
                6: { cellWidth: "auto", textColor: [0, 47, 167] },
            },
        });

        // Footer on every page
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(140, 140, 140);
            doc.text(
                `SERP-Scout · Search Terms Audit · Page ${i} / ${pageCount}`,
                marginX,
                doc.internal.pageSize.getHeight() - 20
            );
        }

        const fname = `search_terms_audit_${Date.now()}.pdf`;
        doc.save(fname);
        toast.success(`Audit PDF downloaded · ${negatives.length + newKeywords.length} findings`);
    };

    return (
        <div className="flex-1 overflow-y-auto thin-scroll bg-white">
            <div className="max-w-7xl mx-auto p-6 lg:p-10">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                    <div>
                        <div className="mono-label">// performance</div>
                        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-ink mt-1">
                            Search Terms Auditor
                        </h1>
                        <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
                            Upload your Google Ads Search terms CSV. We flag waste (cost with zero conversions, junk queries) and surface high-signal terms worth adding as keywords. Export clean lists straight into Google Ads Editor.
                        </p>
                    </div>
                    {normalized.length > 0 && (
                        <div className="flex items-center gap-2">
                            <button
                                data-testid="btn-download-pdf"
                                onClick={handleExportPdf}
                                className="brutal-btn border border-ink bg-white text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                                title="Download findings as PDF"
                            >
                                <FilePdf size={13} weight="bold" />
                                Save as PDF
                            </button>
                            <button
                                data-testid="btn-clear-audit"
                                onClick={clearAll}
                                className="brutal-btn border border-neutral-400 bg-white text-neutral-700 font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                            >
                                <Trash size={13} weight="bold" />
                                Clear
                            </button>
                        </div>
                    )}
                </div>

                {/* Google Ads Import Bar — appears above upload zone AND above loaded state */}
                <AdsImportBar reportKind="search-terms" onFetch={handleFetchFromAds} />

                {/* Upload zone */}
                {!normalized.length && (
                    <div
                        data-testid="upload-zone"
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add("border-klein", "bg-neutral-50");
                        }}
                        onDragLeave={(e) =>
                            e.currentTarget.classList.remove("border-klein", "bg-neutral-50")
                        }
                        onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove("border-klein", "bg-neutral-50");
                            const file = e.dataTransfer.files?.[0];
                            if (file) handleFile(file);
                        }}
                        className="border-2 border-dashed border-neutral-300 p-12 flex flex-col items-center justify-center cursor-pointer hover:border-ink transition text-center"
                    >
                        <UploadSimple size={44} weight="bold" className="text-neutral-400 mb-3" />
                        <div className="font-heading text-lg font-black text-ink">
                            Drop your Search terms <span className="text-klein">CSV</span> here
                        </div>
                        <div className="text-sm text-neutral-500 mt-1 font-mono">
                            or click to browse · CSV only (not PDF) · Google Ads → Reports → Download → CSV
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            data-testid="upload-input"
                            onChange={(e) => handleFile(e.target.files?.[0])}
                        />
                    </div>
                )}

                {/* Loaded summary + config */}
                {normalized.length > 0 && (
                    <div className="border-2 border-ink bg-ink text-white p-4 flex flex-wrap items-center justify-between gap-4 mb-8">
                        <div className="flex items-center gap-3">
                            <FileText size={18} weight="bold" />
                            <div>
                                <div className="font-mono uppercase tracking-widest text-[10px] text-neutral-400">
                                    Loaded
                                </div>
                                <div className="font-heading font-black text-sm" data-testid="loaded-filename">
                                    {filename}{" "}
                                    <span className="text-neutral-400 font-mono">
                                        · {normalized.length} terms
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-6 flex-wrap">
                            <div data-testid="blended-cpa-badge">
                                <div className="font-mono uppercase tracking-widest text-[9px] text-neutral-400 mb-1">
                                    Blended CPA
                                </div>
                                <div className="font-heading font-black text-sm">
                                    {(() => {
                                        const spend = normalized.reduce((s, r) => s + r.cost, 0);
                                        const conv = normalized.reduce((s, r) => s + r.conversions, 0);
                                        return conv > 0 ? (
                                            <span className="text-sky-300">
                                                {(spend / conv).toFixed(2)}
                                            </span>
                                        ) : (
                                            <span className="text-neutral-500">—</span>
                                        );
                                    })()}
                                    <span className="ml-2 text-[10px] font-mono text-neutral-400">
                                        {(() => {
                                            const spend = normalized.reduce((s, r) => s + r.cost, 0);
                                            const conv = normalized.reduce((s, r) => s + r.conversions, 0);
                                            return `${spend.toFixed(0)} / ${conv.toFixed(1)}`;
                                        })()}
                                    </span>
                                </div>
                            </div>
                            <div data-testid="waste-recovery-badge" className="pl-6 border-l border-neutral-700">
                                <div className="font-mono uppercase tracking-widest text-[9px] text-emerald-400 mb-1">
                                    Waste Recovery Estimate
                                </div>
                                <div className="font-heading font-black text-sm">
                                    {(() => {
                                        // Sum spend on currently *selected* negatives; fall back
                                        // to full negatives list so it's non-zero the moment
                                        // suggestions render.
                                        const picked = negatives.filter((n) => selectedNeg[n.term]);
                                        const pool = picked.length ? picked : negatives;
                                        const reclaim = pool.reduce((s, n) => s + (n.cost || 0), 0);
                                        return reclaim > 0 ? (
                                            <span className="text-emerald-300" data-testid="waste-recovery-amount">
                                                {formatMoney(reclaim, accountCurrency)}
                                            </span>
                                        ) : (
                                            <span className="text-neutral-500">—</span>
                                        );
                                    })()}
                                    <span className="ml-2 text-[10px] font-mono text-neutral-400">
                                        {(() => {
                                            const picked = negatives.filter((n) => selectedNeg[n.term]);
                                            const pool = picked.length ? picked : negatives;
                                            const label = picked.length ? "selected" : "if you exclude these";
                                            return `${pool.length} · ${label}`;
                                        })()}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <div className="font-mono uppercase tracking-widest text-[9px] text-neutral-400 mb-1">
                                    High-CTR threshold
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        data-testid="ctr-threshold-slider"
                                        type="range"
                                        min="1"
                                        max="20"
                                        step="0.5"
                                        value={ctrThreshold}
                                        onChange={(e) => setCtrThreshold(parseFloat(e.target.value))}
                                        className="w-28 accent-klein"
                                    />
                                    <span className="font-heading font-black text-sm min-w-[36px]">
                                        {ctrThreshold}%
                                    </span>
                                </div>
                            </div>
                            <div>
                                <div className="font-mono uppercase tracking-widest text-[9px] text-neutral-400 mb-1">
                                    Negatives Level
                                </div>
                                <div className="flex">
                                    {[
                                        { k: "campaign", l: "Campaign" },
                                        { k: "ad_group", l: "Ad Group" },
                                    ].map((opt) => (
                                        <button
                                            key={opt.k}
                                            data-testid={`neg-level-${opt.k}`}
                                            onClick={() => setNegLevel(opt.k)}
                                            className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest font-bold border transition ${
                                                negLevel === opt.k
                                                    ? "bg-klein text-white border-klein"
                                                    : "bg-white text-ink border-neutral-300 hover:border-white"
                                            }`}
                                        >
                                            {opt.l}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="font-mono uppercase tracking-widest text-[9px] text-neutral-400 mb-1">
                                    New KW Match Type
                                </div>
                                <div className="flex">
                                    {["Phrase", "Exact", "Broad"].map((mt) => (
                                        <button
                                            key={mt}
                                            data-testid={`match-type-${mt.toLowerCase()}`}
                                            onClick={() => setMatchType(mt)}
                                            className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest font-bold border transition ${
                                                matchType === mt
                                                    ? "bg-klein text-white border-klein"
                                                    : "bg-white text-ink border-neutral-300 hover:border-white"
                                            }`}
                                        >
                                            {mt}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* View-mode toggle */}
                {normalized.length > 0 && (
                    <div className="mb-4 border-b-2 border-ink flex items-stretch">
                        {[
                            { k: "manual", label: "Manual Review", icon: ListChecks },
                            { k: "auto", label: "Auto Suggest", icon: Sparkle },
                        ].map(({ k, label, icon: Icon }) => (
                            <button
                                key={k}
                                data-testid={`sta-view-${k}`}
                                onClick={() => setViewMode(k)}
                                className={`px-5 py-2.5 flex items-center gap-2 font-mono uppercase tracking-widest text-[11px] font-bold border-r border-ink transition ${
                                    viewMode === k
                                        ? "bg-ink text-white border-b-4 border-b-klein"
                                        : "bg-white text-neutral-600 hover:bg-neutral-50 border-b-4 border-b-transparent"
                                }`}
                            >
                                <Icon size={13} weight="bold" />
                                {label}
                            </button>
                        ))}
                    </div>
                )}

                {/* Manual Review */}
                {normalized.length > 0 && viewMode === "manual" && (
                    <ManualReviewPanel
                        rows={normalized}
                        aiScores={aiScores}
                        aiScoring={aiScoring}
                        aiSummary={aiSummary}
                        businessContext={businessContext}
                        setBusinessContext={setBusinessContext}
                        onAiScore={handleAiScore}
                        rowActions={rowActions}
                        setRowActions={setRowActions}
                        negLevel={negLevel}
                        setNegLevel={setNegLevel}
                        baselineCpc={baselineCpc}
                        setBaselineCpc={setBaselineCpc}
                        onSync={handleSyncActions}
                        syncing={syncing}
                        processedNorms={processedNorms}
                        showProcessed={showProcessed}
                        setShowProcessed={setShowProcessed}
                        accountCurrency={accountCurrency}
                    />
                )}

                {/* QA gate — same-window internal consistency check */}
                {normalized.length > 0 && qaWarnings.length > 0 && (
                    <div
                        data-testid="qa-consistency-banner"
                        className="border-2 border-emerald-500 bg-emerald-50 px-4 py-3 flex items-start gap-3 mb-4"
                    >
                        <ShieldCheck
                            size={18}
                            weight="bold"
                            className="text-emerald-600 mt-0.5 shrink-0"
                        />
                        <div className="flex-1 text-sm text-emerald-900">
                            <div className="font-heading font-black uppercase tracking-tight">
                                Consistency guard removed {qaWarnings.length}{" "}
                                {qaWarnings.length === 1 ? "term" : "terms"} from negatives
                            </div>
                            <ul className="mt-1 space-y-0.5 text-xs leading-relaxed">
                                {qaWarnings.slice(0, 6).map((w, i) => (
                                    <li key={i}>
                                        <span className="font-bold">{w.term}</span> — {w.reason}
                                    </li>
                                ))}
                                {qaWarnings.length > 6 && (
                                    <li className="text-emerald-700">
                                        …and {qaWarnings.length - 6} more.
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>
                )}

                {/* Auto Suggest — legacy dual-table view */}
                {normalized.length > 0 && viewMode === "auto" && (
                    <div className="grid grid-cols-1 gap-8">
                        <AuditTable
                            title="Suggested Negatives"
                            icon={<Prohibit size={18} weight="bold" className="text-red-600" />}
                            testId="table-negatives"
                            rows={negatives}
                            selected={selectedNeg}
                            onToggle={(term) =>
                                setSelectedNeg((p) => ({ ...p, [term]: !p[term] }))
                            }
                            onToggleAll={toggleAllNeg}
                            emptyText="No wasteful search terms found. Nice."
                            showReasons
                            reasonLabel="Reason"
                            reasonColor="text-red-700"
                            exportBtn={
                                <button
                                    data-testid="btn-export-negatives"
                                    onClick={handleExportNegatives}
                                    className="brutal-btn border border-ink bg-ink text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                                >
                                    <DownloadSimple size={13} weight="bold" />
                                    Export Selected Negatives · {negCount}
                                </button>
                            }
                        />

                        <AuditTable
                            title="Suggested New Keywords"
                            icon={<Sparkle size={18} weight="bold" className="text-klein" />}
                            testId="table-new-keywords"
                            rows={newKeywords}
                            selected={selectedKw}
                            onToggle={(term) =>
                                setSelectedKw((p) => ({ ...p, [term]: !p[term] }))
                            }
                            onToggleAll={toggleAllKw}
                            emptyText={`No terms cleared the ${ctrThreshold}% CTR / ≥1 conversion bar.`}
                            showReasons
                            reasonLabel="Signal"
                            reasonColor="text-klein"
                            exportBtn={
                                <button
                                    data-testid="btn-export-new-keywords"
                                    onClick={handleExportNewKeywords}
                                    className="brutal-btn border border-klein bg-klein text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                                >
                                    <DownloadSimple size={13} weight="bold" />
                                    Export Selected · {kwCount} · {matchType}
                                </button>
                            }
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------------- */
/* Sub-component: audit table                                                 */
/* ------------------------------------------------------------------------- */

const AuditTable = ({
    title,
    icon,
    testId,
    rows,
    selected,
    onToggle,
    onToggleAll,
    emptyText,
    showReasons,
    reasonLabel,
    reasonColor,
    exportBtn,
}) => {
    const allSelected = rows.length > 0 && rows.every((r) => selected[r.term]);
    return (
        <section data-testid={testId} className="border border-neutral-300 bg-white">
            <div className="flex items-center justify-between p-4 border-b-2 border-ink flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    {icon}
                    <h2 className="font-heading text-lg font-black uppercase tracking-tight text-ink">
                        {title}
                    </h2>
                    <span className="mono-label ml-2">{rows.length}</span>
                </div>
                {exportBtn}
            </div>

            {rows.length === 0 ? (
                <div className="p-8 text-center">
                    <ChartBar size={32} weight="bold" className="text-neutral-300 mx-auto mb-2" />
                    <div className="text-sm text-neutral-500">{emptyText}</div>
                </div>
            ) : (
                <div className="overflow-x-auto max-h-[520px] thin-scroll">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-100 sticky top-0 z-10">
                            <tr className="text-left">
                                <th className="px-3 py-2 w-8">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={(e) => onToggleAll(e.target.checked)}
                                        className="accent-klein"
                                        data-testid={`${testId}-toggle-all`}
                                    />
                                </th>
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold">
                                    Search Term
                                </th>
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                    Clicks
                                </th>
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                    Cost
                                </th>
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                    CTR
                                </th>
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                    Conv.
                                </th>
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right whitespace-nowrap">
                                    Cost / Conv.
                                </th>
                                {showReasons && (
                                    <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold">
                                        {reasonLabel}
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr
                                    key={row.term}
                                    className="border-t border-neutral-200 hover:bg-neutral-50"
                                    data-testid={`${testId}-row`}
                                >
                                    <td className="px-3 py-2">
                                        <input
                                            type="checkbox"
                                            checked={!!selected[row.term]}
                                            onChange={() => onToggle(row.term)}
                                            className="accent-klein"
                                        />
                                    </td>
                                    <td className="px-3 py-2 text-ink font-medium">{row.term}</td>
                                    <td className="px-3 py-2 text-right font-mono text-ink">
                                        {row.clicks}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-ink">
                                        {row.cost.toFixed(2)}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-ink">
                                        {row.ctr.toFixed(1)}%
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-ink">
                                        {row.conversions}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-ink whitespace-nowrap">
                                        {row.cost_per_conv !== null && row.cost_per_conv !== undefined ? (
                                            <span className={row.cost_per_conv > 50 ? "text-red-700" : ""}>
                                                {row.cost_per_conv.toFixed(2)}
                                            </span>
                                        ) : (
                                            <span className="text-neutral-400">—</span>
                                        )}
                                    </td>
                                    {showReasons && (
                                        <td className={`px-3 py-2 font-mono text-[11px] ${reasonColor}`}>
                                            {row.tier && (
                                                <span
                                                    className={`inline-block mr-1.5 px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold border ${
                                                        row.tier === "high"
                                                            ? "border-red-600 bg-red-600 text-white"
                                                            : row.tier === "medium"
                                                            ? "border-amber-500 bg-amber-100 text-amber-900"
                                                            : "border-neutral-400 bg-neutral-100 text-neutral-700"
                                                    }`}
                                                    title={
                                                        row.tier === "high"
                                                            ? "High confidence — named competitor or directory."
                                                            : row.tier === "medium"
                                                            ? "Medium confidence — matched a negative-keyword pattern."
                                                            : "Low confidence — spend-based signal only. Review before applying."
                                                    }
                                                >
                                                    {row.tier}
                                                </span>
                                            )}
                                            {row.reasons.join(" · ")}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

/* ------------------------------------------------------------------------- */
/* Manual Review — unified table + AI scoring                                 */
/* ------------------------------------------------------------------------- */

const SCORE_COLORS = {
    high: "bg-emerald-100 text-emerald-800 border-emerald-400",
    mid: "bg-yellow-100 text-yellow-900 border-yellow-400",
    low: "bg-red-100 text-red-800 border-red-400",
    none: "bg-neutral-100 text-neutral-500 border-neutral-300",
};

const scoreBucket = (s) => {
    if (s == null) return "none";
    if (s >= 8) return "high";
    if (s >= 5) return "mid";
    return "low";
};

const ManualReviewPanel = ({
    rows,
    aiScores,
    aiScoring,
    aiSummary,
    businessContext,
    setBusinessContext,
    onAiScore,
    rowActions,
    setRowActions,
    negLevel,
    setNegLevel,
    baselineCpc,
    setBaselineCpc,
    onSync,
    syncing,
    processedNorms,
    showProcessed,
    setShowProcessed,
    accountCurrency,
}) => {
    const processedSet = useMemo(
        () => new Set(processedNorms || []),
        [processedNorms]
    );

    const scored = useMemo(() => {
        return rows.map((r) => {
            const s = aiScores[r.term.toLowerCase()];
            const norm = r.term.trim().toLowerCase().replace(/\s+/g, " ");
            return {
                ...r,
                relevance_score: s?.relevance_score ?? null,
                is_candidate_junk: !!s?.is_candidate_junk,
                ai_reason: s?.reason || null,
                processed: processedSet.has(norm),
            };
        });
    }, [rows, aiScores, processedSet]);

    const visibleRows = useMemo(() => {
        const filtered = showProcessed ? scored : scored.filter((r) => !r.processed);
        const copy = [...filtered];
        copy.sort((a, b) => {
            if (a.is_candidate_junk !== b.is_candidate_junk) return a.is_candidate_junk ? -1 : 1;
            const sa = a.relevance_score ?? 999;
            const sb = b.relevance_score ?? 999;
            if (sa !== sb) return sa - sb;
            return (b.cost || 0) - (a.cost || 0);
        });
        return copy;
    }, [scored, showProcessed]);

    const setAction = (term, next) => {
        setRowActions((prev) => {
            const cur = prev[term]?.action;
            if (!next || cur === next?.action) {
                // Toggle off (click same action again = clear).
                const { [term]: _drop, ...rest } = prev; // eslint-disable-line no-unused-vars
                return rest;
            }
            return { ...prev, [term]: next };
        });
    };

    const counts = useMemo(() => {
        const c = { negative: 0, keyword_exact: 0, keyword_phrase: 0, exact_low_bid: 0 };
        for (const t of Object.keys(rowActions)) {
            const a = rowActions[t]?.action;
            if (a && c[a] !== undefined) c[a] += 1;
        }
        return c;
    }, [rowActions]);

    const totalAssigned =
        counts.negative + counts.keyword_exact + counts.keyword_phrase + counts.exact_low_bid;
    const junkCount = scored.filter((r) => r.is_candidate_junk && !r.processed).length;
    const processedCount = scored.filter((r) => r.processed).length;

    const bulkAssignJunk = () => {
        setRowActions((prev) => {
            const next = { ...prev };
            for (const r of scored) {
                if (r.is_candidate_junk && !r.processed && !next[r.term]) {
                    next[r.term] = { action: "negative" };
                }
            }
            return next;
        });
    };

    const bulkClear = () => setRowActions({});

    return (
        <div className="space-y-4" data-testid="sta-manual-panel">
            {/* AI scoring bar */}
            <div className="border-2 border-ink bg-yellow-50 p-3 flex flex-col md:flex-row md:items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                    <label className="mono-label mb-1 block flex items-center gap-1">
                        <Sparkle size={11} weight="bold" className="text-klein" />
                        Business context
                        <span className="text-neutral-500 normal-case ml-1 font-normal">
                            (helps Claude score relevance for THIS client)
                        </span>
                    </label>
                    <input
                        data-testid="sta-business-context"
                        value={businessContext}
                        onChange={(e) => setBusinessContext(e.target.value)}
                        placeholder="e.g. Water softener installation & service in Bengaluru"
                        className="w-full h-9 text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3"
                    />
                </div>
                <div>
                    <label className="mono-label mb-1 block">Baseline CPC ({currencySymbol(accountCurrency) || "£"})</label>
                    <input
                        data-testid="sta-baseline-cpc"
                        type="number"
                        min="0.01"
                        step="0.05"
                        value={baselineCpc}
                        onChange={(e) => setBaselineCpc(e.target.value)}
                        className="w-24 h-9 text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-2"
                    />
                </div>
                <button
                    data-testid="sta-ai-score"
                    onClick={onAiScore}
                    disabled={aiScoring || !rows.length}
                    className="brutal-btn h-10 px-4 border border-klein bg-klein text-white hover:bg-klein/90 font-mono uppercase tracking-widest text-[11px] font-bold flex items-center gap-1.5 disabled:opacity-40"
                >
                    {aiScoring ? (
                        <ArrowsClockwise size={13} weight="bold" className="animate-spin" />
                    ) : (
                        <Sparkle size={13} weight="bold" />
                    )}
                    {Object.keys(aiScores).length ? "Re-score with AI" : "Score with AI"}
                </button>
                {aiSummary && (
                    <div
                        data-testid="sta-ai-summary"
                        className="text-[11px] font-mono text-neutral-700 flex items-center gap-3 md:ml-2"
                    >
                        <span className="text-emerald-800">
                            <ChartBar size={11} weight="bold" className="inline -mt-0.5 mr-1" />
                            {aiSummary.high_relevance || 0} high (8-10)
                        </span>
                        <span className="text-red-800">
                            <Warning size={11} weight="bold" className="inline -mt-0.5 mr-1" />
                            {aiSummary.junk_flagged || 0} junk-flagged
                        </span>
                    </div>
                )}
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2 border border-neutral-300 bg-neutral-50 px-3 py-2">
                <button
                    data-testid="sta-manual-mark-junk-negative"
                    onClick={bulkAssignJunk}
                    disabled={!junkCount}
                    className="h-7 px-2 border border-red-500 text-red-700 bg-white hover:bg-red-500 hover:text-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
                >
                    <Warning size={10} weight="bold" /> Mark AI-junk as neg ({junkCount})
                </button>
                <button
                    data-testid="sta-manual-clear-actions"
                    onClick={bulkClear}
                    disabled={!totalAssigned}
                    className="h-7 px-2 border border-neutral-400 text-neutral-700 bg-white hover:bg-neutral-200 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
                >
                    Clear actions
                </button>
                <div className="flex-1" />
                {processedCount > 0 && (
                    <button
                        data-testid="sta-toggle-processed"
                        onClick={() => setShowProcessed(!showProcessed)}
                        className="h-7 px-2 border border-ink text-ink bg-white hover:bg-ink hover:text-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1"
                    >
                        {showProcessed ? <EyeSlash size={10} weight="bold" /> : <Eye size={10} weight="bold" />}
                        {showProcessed ? "Hide" : "Show"} processed ({processedCount})
                    </button>
                )}
                <span className="text-[11px] font-mono text-ink">
                    <ListChecks size={12} weight="bold" className="inline -mt-0.5 mr-1" />
                    {totalAssigned} of {visibleRows.length} assigned
                </span>
            </div>

            {/* Table */}
            <div className="border-2 border-ink overflow-hidden">
                <div className="max-h-[560px] overflow-y-auto thin-scroll">
                    <table className="w-full text-sm">
                        <thead className="bg-ink text-white sticky top-0 z-10">
                            <tr>
                                <th className="px-3 py-2 text-left mono-label">Term</th>
                                <th className="px-3 py-2 text-right mono-label w-16">Clicks</th>
                                <th className="px-3 py-2 text-right mono-label w-20">Cost</th>
                                <th className="px-3 py-2 text-right mono-label w-16">Conv</th>
                                <th className="px-3 py-2 text-center mono-label w-24">AI Score</th>
                                <th className="px-3 py-2 text-left mono-label w-28">Flag</th>
                                <th className="px-3 py-2 text-center mono-label w-96">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map((r) => {
                                const bucket = scoreBucket(r.relevance_score);
                                const cur = rowActions[r.term]?.action;
                                return (
                                    <tr
                                        key={r.term}
                                        data-testid={`sta-row-${r.term}`}
                                        className={`border-b border-neutral-100 hover:bg-neutral-50 ${r.processed ? "opacity-40" : ""} ${r.is_candidate_junk && !r.processed ? "bg-red-50/40" : ""}`}
                                    >
                                        <td className="px-3 py-1.5 text-ink">{r.term}</td>
                                        <td className="px-3 py-1.5 text-right font-mono text-ink">{r.clicks ?? 0}</td>
                                        <td className="px-3 py-1.5 text-right font-mono text-ink">{(r.cost || 0).toFixed(2)}</td>
                                        <td className="px-3 py-1.5 text-right font-mono text-ink">{(r.conversions || 0).toFixed(1)}</td>
                                        <td className="px-3 py-1.5 text-center">
                                            <span
                                                className={`inline-block min-w-[28px] px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest font-bold border ${SCORE_COLORS[bucket]}`}
                                                data-testid={`sta-score-${r.term}`}
                                            >
                                                {r.relevance_score ?? "—"}
                                                {r.relevance_score != null && <span className="opacity-60">/10</span>}
                                            </span>
                                        </td>
                                        <td className="px-3 py-1.5">
                                            {r.processed ? (
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest font-bold border border-emerald-400 bg-emerald-50 text-emerald-800">
                                                    ✓ Synced
                                                </span>
                                            ) : r.is_candidate_junk ? (
                                                <span
                                                    data-testid={`sta-junk-${r.term}`}
                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest font-bold border border-red-400 bg-red-500 text-white"
                                                >
                                                    <Warning size={9} weight="bold" /> Candidate
                                                </span>
                                            ) : (
                                                <span className="text-neutral-400 text-[10px]">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-1.5">
                                            {r.processed ? (
                                                <span className="text-neutral-400 text-[10px]">—</span>
                                            ) : (
                                                <ActionButtons
                                                    term={r.term}
                                                    current={cur}
                                                    onSet={setAction}
                                                    matchType={rowActions[r.term]?.matchType || "Exact"}
                                                />
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Bottom sync bar */}
            <div className="border-2 border-ink bg-ink text-white p-4 flex items-center flex-wrap gap-3">
                <div className="text-[11px] font-mono uppercase tracking-widest flex items-center gap-3">
                    <span data-testid="sta-count-neg">
                        <Prohibit size={11} weight="bold" className="inline -mt-0.5 mr-1 text-red-400" />
                        {counts.negative} neg
                    </span>
                    <span data-testid="sta-count-kw">
                        <Plus size={11} weight="bold" className="inline -mt-0.5 mr-1 text-emerald-400" />
                        {counts.keyword_exact + counts.keyword_phrase} kw
                    </span>
                    <span data-testid="sta-count-lowbid">
                        <ArrowClockwise size={11} weight="bold" className="inline -mt-0.5 mr-1 text-amber-400" />
                        {counts.exact_low_bid} low-bid
                    </span>
                </div>
                <div className="flex items-center gap-2 ml-2">
                    <span className="mono-label text-neutral-400">Neg level:</span>
                    {[
                        { k: "campaign", l: "Campaign" },
                        { k: "ad_group", l: "Ad-group" },
                    ].map((opt) => (
                        <button
                            key={opt.k}
                            data-testid={`sta-manual-neglevel-${opt.k}`}
                            onClick={() => setNegLevel(opt.k)}
                            className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest font-bold border transition ${
                                negLevel === opt.k
                                    ? "bg-klein text-white border-klein"
                                    : "bg-transparent text-neutral-300 border-neutral-600 hover:border-white"
                            }`}
                        >
                            {opt.l}
                        </button>
                    ))}
                </div>
                <div className="flex-1" />
                <button
                    data-testid="sta-sync"
                    onClick={onSync}
                    disabled={!totalAssigned || syncing}
                    className="brutal-btn h-11 px-6 border border-emerald-500 bg-emerald-600 text-white hover:bg-emerald-500 font-mono uppercase tracking-widest text-[12px] font-bold flex items-center gap-2 disabled:opacity-40"
                >
                    {syncing ? (
                        <ArrowsClockwise size={15} weight="bold" className="animate-spin" />
                    ) : (
                        <CloudArrowUp size={15} weight="bold" />
                    )}
                    Sync Changes to Google Ads ({totalAssigned})
                </button>
            </div>
        </div>
    );
};

// ---- 3 per-row action buttons ----
const ActionButtons = ({ term, current, onSet, matchType }) => {
    const activeCls = "ring-2 ring-offset-1";
    return (
        <div className="flex items-center justify-center gap-1 flex-wrap">
            <button
                data-testid={`sta-act-neg-${term}`}
                onClick={() => onSet(term, current === "negative" ? null : { action: "negative" })}
                className={`h-7 px-2 border border-red-600 bg-red-600 text-white hover:bg-red-700 font-mono uppercase tracking-widest text-[9px] font-bold flex items-center gap-1 ${current === "negative" ? activeCls + " ring-red-300" : "opacity-80 hover:opacity-100"}`}
                title="Add as Negative — blocks this term"
            >
                <Prohibit size={9} weight="bold" /> Neg
            </button>
            <div className="flex items-stretch">
                <button
                    data-testid={`sta-act-kw-${term}`}
                    onClick={() =>
                        onSet(
                            term,
                            current && current.startsWith("keyword")
                                ? null
                                : { action: `keyword_${(matchType || "Exact").toLowerCase()}`, matchType: matchType || "Exact" }
                        )
                    }
                    className={`h-7 px-2 border border-emerald-700 bg-emerald-600 text-white hover:bg-emerald-700 font-mono uppercase tracking-widest text-[9px] font-bold flex items-center gap-1 border-r-0 ${current && current.startsWith("keyword") ? activeCls + " ring-emerald-300" : "opacity-80 hover:opacity-100"}`}
                    title="Add as Keyword — bid on this term"
                >
                    <Plus size={9} weight="bold" /> KW
                </button>
                {current && current.startsWith("keyword") && (
                    <select
                        data-testid={`sta-act-kw-match-${term}`}
                        value={matchType || "Exact"}
                        onChange={(e) =>
                            onSet(term, {
                                action: `keyword_${e.target.value.toLowerCase()}`,
                                matchType: e.target.value,
                            })
                        }
                        className="h-7 border border-emerald-700 border-l-0 bg-emerald-50 text-emerald-900 text-[9px] font-mono uppercase tracking-widest font-bold px-1 focus:outline-none"
                    >
                        <option value="Exact">Exact</option>
                        <option value="Phrase">Phrase</option>
                    </select>
                )}
            </div>
            <button
                data-testid={`sta-act-lowbid-${term}`}
                onClick={() => onSet(term, current === "exact_low_bid" ? null : { action: "exact_low_bid" })}
                className={`h-7 px-2 border border-amber-700 bg-amber-500 text-white hover:bg-amber-600 font-mono uppercase tracking-widest text-[9px] font-bold flex items-center gap-1 ${current === "exact_low_bid" ? activeCls + " ring-amber-300" : "opacity-80 hover:opacity-100"}`}
                title="Add as Exact match with a 50%-lower CPC bid"
            >
                <ArrowClockwise size={9} weight="bold" /> Exact -50%
            </button>
        </div>
    );
};

